/**
 * ReviewView.tsx — the clinician review of one submitted case.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.5 (clinician + admin).
 *
 * WHY: a clinician needs everything to make the clinical decision on one screen:
 *   - the SAFETY ACKNOWLEDGEMENT band (2026-08-19): did anyone show this
 *     patient what the medicine can do to them before they paid? A missing
 *     acknowledgement is shown LOUDLY — see SafetyAckBand.tsx;
 *   - the KEY ALERTS banner (2026-08-07) directly under the R31 crisis banner:
 *     is this patient ALREADY on medication? The same person can arrive by
 *     self-serve, callback or back office, so nobody on this screen otherwise
 *     knows. It ALERTS, never blocks (legitimate switches exist) — see
 *     KeyAlerts.tsx. The read lives here, at the view; the banner is pure;
 *   - the SCR quick-copy block PINNED at the top (names / DOB / postcode + the
 *     SCR-check consent status) for the Spine check. Every state of that slot,
 *     including the plain "no account yet" explanation, is ScrPanel's (typed in
 *     scrPanelState.ts, 2026-09-25);
 *   - a STRUCTURED questionnaire display (eligibility / safety / weight / meds /
 *     preference / consent sections) — never raw JSON — with BMI, the soft-stop
 *     flags and prior treatment;
 *   - the submitted weight photos, in their own card: a GALLERY of every photo
 *     the patient has uploaded (2026-08-27) with the ATTESTATION control
 *     beside it — see WeightPhotoSection;
 *   - a "Previously issued prescriptions" history panel below the decision
 *     actions — see PrescriptionHistory — refetched after a successful issue so
 *     the new prescription appears immediately. V1.5 auto-issue (orders-service):
 *     month-1 capture on APPROVE already creates the Rx, so this panel is also
 *     refetched after approve, and the decision notice must say the prescription
 *     was issued rather than prompting the clinician to issue it again;
 *   - the read-only delivery address (needed to approve the fulfilment);
 *   - the decision actions, state-gated: approve → prescribe (prescribe is
 *     enabled ONLY after approval, mirroring the server approval gate) or
 *     reject. (The old callback-lane £49 charge-consult / re-entry actions are
 *     GONE — the fee and its endpoints were removed 2026-07-21; callback-lane
 *     conversions now ride the plan-link / pre-approved payment lanes.) The
 *     success/error notice sits IN the Decision card — that is where the
 *     clinician just clicked, and the review is long enough that a banner at
 *     the top of the tab is easy to miss. The deal drawer also holds a copy so
 *     the notice survives a parent reload (approve moves the deal to Won).
 *
 * CONFIRMATIONS (G2): every clinical decision pauses on the shared
 * ConfirmDialog before its POST fires — approve with plain copy, prescribe
 * with a line summary, and reject with a REQUIRED reason that is carried on
 * the wire ({reason}) and recorded in the server-side audit log.
 *
 * MONEY-REDACTION (Reshape §3.1): NOTHING on this surface renders or requests
 * a price, amount, pence or payment status. The clinician-projected
 * queue/SCR/review/prescription-history shapes carry no money by
 * construction. A test scans this surface for money and asserts none.
 *
 * 2026-07-21 CRM redesign: this view now mounts inside the deal drawer's
 * Review tab (the pipeline replaced the standalone case queue); `onBack`
 * returns to the drawer's Overview tab.
 *
 * 2026-08-27 drawer restructure (CTO stage walkthrough): the ASSESSMENT
 * clinical-notes thread (AssessmentNotesSection — list + composer) moved HERE
 * from the drawer's Activity tab, below the decision actions, so a clinician
 * decides and documents in ONE place (Activity is the comms trail only now).
 * On an order-backed review it sits beside the per-case CaseNotesSection and
 * is therefore titled "Assessment notes" (two cards both named "Clinical
 * notes" would be ambiguous); the order-less review keeps the established
 * "Clinical notes" name — it is the only thread on that screen. Same
 * component, same store, same author-only edit rule — no role gate, exactly
 * as the Activity-tab mount had it. StructuredQuestionnaire is also EXPORTED
 * now: the drawer's Questionnaire tab renders the same server sections as its
 * read-only answer summary (QuestionnaireSummary) instead of a disabled form
 * replica, with `photoState` swapping the q14 item's stored answer for the
 * ACTUAL photo state and `renderItemExtra` threading the per-answer note
 * affordance through the summary.
 *
 * 2026-08-27 CLINICIAN PHOTO REVIEW (CTO + Reneal): "only after the clinician
 * SEES the photo can they finalise everything — clinician checks, finalise,
 * card gets charged." Three things landed on this screen:
 *   - the weight-photo card became a GALLERY of EVERY uploaded photo (patients
 *     can now upload several, including after submission), each a short-lived
 *     signed url that enlarges in the shared DialogShell;
 *   - an ATTESTATION control beside it (CLINICIAN only — attesting is a
 *     clinical act, and the server's @Roles agrees), which stamps
 *     photosReviewedAt/By on the order case;
 *   - the APPROVE control is disabled, with the reason in words, until that
 *     stamp exists. R41: the SERVER is the control — approve 409s
 *     (PhotosNotReviewedError) on an assessment-lane order regardless of what
 *     this screen renders. The disabled state exists so a clinician learns WHY
 *     from the screen instead of from a failed action.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { getAssessmentPhotos, getAssessmentReview, requestWeightEvidence, retractWeightPhoto } from '../api/clinic';
import { CRISIS_LINES } from '../journey/signposting';
import { getScr, lookupStaffUsers } from '../api/identity';
import {
  approveOrder,
  getCasePrescriptions,
  getPhotoReviewStatus,
  markPhotosReviewed,
  prescribe,
  rejectOrder,
} from '../api/orders';
import { ApiError } from '../api/client';
import type {
  AssessmentPhotoEntry,
  AssessmentPhotosRead,
  AssessmentReview,
  MhRiskCategory,
  PhotoReviewStatus,
  PriorStop,
  PrescriptionIssueRequest,
  PrescriptionItemInput,
  StaffOrder,
  StaffPrescriptionsResponse,
} from '../api/types';
import { DEFAULT_CLINIC_SLUG } from '../admin/clinics';
import { useAuth } from '../auth/AuthContext';
import { formatAddress, formatPatientName, formatUtcDate, formatUtcDateTime, orderDisplayRef } from '../lib/format';
import { Row, SectionTitle } from '../staff/ui';
import { PatientPlanCard } from './PatientPlanCard';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { NOT_RECORDED, resolvePrescribedLine } from '../lib/prescribedLine';
import { PrescribedItems } from '../ui/PrescribedItems';
import { PrescribedSupplies, SupplyLines, type SupplyLine } from '../ui/PrescribedSupplies';
import { PhotoLightbox, photoAlt } from '../photos/PhotoLightbox';
import {
  WeightPhotoCapLine,
  WeightPhotoPicker,
  WeightPhotoTiles,
  useWeightPhotoUploads,
} from '../photos/WeightPhotoUploads';
import { weightAskState, weightDeclarationView } from './weightDeclaration';
import { Alert, Badge, Button, Card, ErrorState, Help, Spinner, CheckIcon, ChevronLeftIcon } from '../ui/kit';
/* Decorative glyphs on the two staff evidence lanes — the button text still
   carries each accessible name (see ui/icons.tsx). They are deliberately
   different SHAPES rather than two arrows: one adds evidence here, the other
   emails the patient. */
import { IconRequestPhotos, IconUploadPhotos } from '../ui/icons';
import { AssessmentNotesSection } from './AssessmentNotes';
import { CaseNotesSection } from './CaseNotes';
import { KeyAlertsSection, type MedicationSafetyView } from './KeyAlerts';
import { SafetyAckBand } from './SafetyAckBand';
import { useMedicationSafety } from './useMedicationSafety';
import { PrescribeForm } from './PrescribeForm';
import { NO_SUPPLIES_COPY } from './SuppliesSection';
import {
  PrescribingFlagChip,
  PrescribingReasonNotice,
  prescribingFlagsOf,
  type PrescribingFlags,
} from './prescribingFlags';
import { isClinicianPrescribeState } from './prescribeStates';
import { RePrescribeModal, type ReprescribeSource } from './RePrescribeModal';
import { ScrPanel } from './ScrPanel';
import { scrStateFromError, type ScrPanelState } from './scrPanelState';

type Loadable<T> = { state: 'loading' | 'ready' | 'error' | 'skip'; data: T | null };

const LANE_LABEL: Record<string, string> = { form: 'Form journey', callback: 'Callback journey' };

/**
 * A decision awaiting its ConfirmDialog (G2): every clinical action pauses on
 * an explicit confirmation before the POST fires — approve with plain copy,
 * prescribe with a line summary, reject with a REQUIRED reason that lands in
 * the audit log.
 */
type PendingDecision =
  | { kind: 'approve' }
  | { kind: 'reject' }
  /**
   * `summary` is the FORM's own human description of each line — including the
   * licensed description of the chosen strength, which the request body does not
   * carry (it carries a key, because the server resolves it). It is display-only
   * and never posted. `supplies` is the same idea for the supplies section —
   * `{ quantity, name }` per supply, with the SERVER's names (the body carries
   * only keys).
   */
  | { kind: 'prescribe'; body: PrescriptionIssueRequest; summary: string[]; supplies: SupplyLine[] };

/**
 * The prescribe confirmation body.
 *
 * It prefers the FORM'S OWN summary, which names the licensed variant the
 * clinician chose — the exact description the pharmacist will read. The request
 * body cannot supply that: it carries a variant KEY and the server resolves it,
 * so summarising the body alone would show a prescriber an opaque slug (or, as
 * it used to, no strength at all) on the last screen before a document is
 * signed. The body-only form remains as the fallback for a caller with no
 * summary to give.
 *
 * EXPORTED for `lib/resolverAgreement.spec.tsx` alone. This sentence is the last
 * thing a prescriber reads before a document is signed, so it is one of the
 * surfaces that must agree with every other — and it is a STRING rather than a
 * rendered block, so the only way to hold it to that is to call it.
 */
export function summariseForConfirm(items: PrescriptionItemInput[], summary?: string[]): string {
  // ⚠️ THE FALLBACK USED TO BE `${item.medicationName} × ${item.quantity}`.
  //
  // On the last screen before a prescription is SIGNED, that printed whatever
  // string sat in `medicationName` — including the raw product key, on every
  // line the deleted auto-issue lane had written — as though it were the
  // medication a prescriber had chosen, next to a bare integer with no unit.
  // Both halves now come from the one resolver: a name nobody chose reads
  // "Not recorded" here exactly as it does on every other staff surface, and
  // the quantity is the server's own wording.
  //
  // `PrescriptionItemInput` is the POST shape and carries no `variant` and no
  // `displayQuantity` — the server resolves both — so the resolver is fed the
  // stored fields alone and the flag is the honest answer for a line the form
  // could not name. In practice the FORM's own `summary` is what renders (it
  // names the licensed variant the clinician just picked); this is the
  // fallback for a caller with none.
  const lines = summary?.length
    ? summary
    : items.map((item) => {
        const line = resolvePrescribedLine({ ...item, dosage: item.dosage ?? null, variant: null });
        return `${line.name ?? NOT_RECORDED} × ${line.quantityText ?? NOT_RECORDED}`;
      });
  return `${items.length} item(s) — ${lines.join(', ')}`;
}

export type ActionNotice = { tone: 'success' | 'danger'; text: string };

/**
 * Stable ids for the two blocked-reason paragraphs the disabled controls point
 * at with `aria-describedby` (WCAG 2.1 AA 1.4.1 + 3.3.1 — never colour alone,
 * and never a bare `disabled` with no explanation). The pattern is the
 * checkout pay gate's, unchanged.
 */
const ATTEST_BLOCKED_REASON_ID = 'photo-attest-blocked-reason';
const APPROVE_BLOCKED_REASON_ID = 'approve-photo-gate-reason';

/** The approve control's reason while the photos-reviewed gate is unmet. */
const APPROVE_PHOTO_GATE_REASON = 'Review the patient photos first';

/** The house line for a failed decision action with nothing readable to say. */
const ACTION_FAILED_NOTICE = 'Sorry — that action could not be completed. Please try again.';

/**
 * The SERVER'S OWN sentence when it sent one, the house line otherwise.
 *
 * WHY this exists at all: the photos-reviewed gate can leave a clinician facing
 * a refusal the screen never predicted — the gate's status read lives on one
 * request and approve on another, so a failed status read leaves approve
 * enabled (deliberately: R41, the UI must not invent a block) and the 409 is
 * the FIRST time the gate is mentioned. "That action could not be completed"
 * reads as a network blip and sends the clinician to retry the same refusal;
 * "…the photos have not been confirmed as reviewed" tells them the one thing
 * they can act on. Same rule the attestation's two 409s already follow.
 *
 * Only for routes whose refusal bodies are PHI-free by construction — see the
 * note on `approveOrder` in api/orders.ts.
 */
function serverSentenceOr(err: unknown, fallback: string): string {
  return err instanceof ApiError && err.detail ? err.detail : fallback;
}

/**
 * Post-issue copy.
 *
 * ⚠️ ONLY THE PRESCRIBE PATH MAY CLAIM THE PHARMACY SEND, because only it
 * observes one: `prescribe()` returns `pharmacySent`. The APPROVE path learns
 * that a prescription exists by re-reading the history, and
 * `StaffPrescriptionsResponse` carries no send fact at all — so from there the
 * send is unobserved, and saying "sent to the pharmacy" would be the UI
 * asserting a third-party delivery nobody watched.
 *
 * That is not a hypothetical failure mode. On 2026-08-18 a production dispense
 * email was found to have been going to the wrong mailbox for weeks: every
 * internal signal looked healthy, so nothing prompted anyone to check. A
 * clinician told "sent to the pharmacy" has no reason to chase it; one told
 * "Prescription issued" still sees the truth and nothing is hidden from them.
 * Under-claim here, always.
 */
function prescriptionIssuedNotice(pharmacySent: boolean): ActionNotice {
  return {
    tone: 'success',
    text: pharmacySent
      ? 'Prescription issued and sent to the pharmacy for home delivery.'
      : 'Prescription issued.',
  };
}

export function ReviewView({
  order,
  assessmentId,
  onBack,
  onActioned,
  recoveredNotice,
  safety: hoistedSafety,
  onRetrySafety,
  consentAgreedAt,
  consentQuestionSetVersion,
}: {
  /** The order case (full mode — the decision actions). Absent for a submitted
   *  assessment with no order yet (review-only mode; item 2, 2026-07-28). */
  order?: StaffOrder | null;
  /** The assessment to review when there is no order (`order.assessmentRef` wins). */
  assessmentId?: string;
  onBack: () => void;
  /** Fired after a successful approve/reject/prescribe. The notice rides with
   *  it so the deal drawer can persist the banner across a detail reload
   *  (approve moves the deal to Won and remounts this view). */
  onActioned?: (notice?: ActionNotice) => void;
  /** Drawer-held copy of the last success notice — wins over local state so a
   *  remount still shows "Prescription issued." rather than a blank Decision. */
  recoveredNotice?: ActionNotice | null;
  /**
   * The key-alerts answer, HOISTED by an owner that needs it too (2026-08-07
   * Phase 2: the deal drawer flags its Review tab from the same answer, before
   * anyone opens the tab). When given, this view issues NO check of its own —
   * one read, one answer, so the tab flag and the banner can never disagree.
   * Absent on a standalone mount, where this view owns the read as before.
   */
  safety?: MedicationSafetyView;
  /** Retry for the hoisted answer; required for the failed-check "Try again". */
  onRetrySafety?: () => void;
  /**
   * When the patient ticked the consent declarations (2026-08-26 §2) — from
   * the deal detail's assessment block (the drawer holds it; the review read
   * carries no timestamp). Absent/null renders nothing: an older server, or a
   * standalone mount with no deal context, must not imply "never agreed".
   */
  consentAgreedAt?: string | null;
  /**
   * WHICH QUESTION-SET VERSION THOSE TICKS BELONG TO (clinic-service's
   * `consentQuestionSetVersion`, on the deal detail beside `consentAgreedAt`).
   *
   * ⚠️ IT IS THE RECONSTRUCTION KEY, WHICH IS WHY IT IS ON SCREEN AT ALL. The
   * weight declaration is an undertaking the patient gave in specific words,
   * and those words are versioned rather than copied onto every row — a
   * per-patient copy of prose that is identical for thousands of people is a
   * per-patient opportunity for it to disagree with itself. Question sets are
   * ADDITIVE and each `Assessment` keeps its own `questionSetId`, and
   * clinic-service's rule is that any edit to the declaration's content BUMPS
   * the set version in the same commit (the seeder is create-when-absent, so an
   * unbumped edit ships nothing). So this integer is what lets anyone —
   * a prescriber next month, an auditor next year — turn "they agreed" back
   * into the sentences they agreed to.
   *
   * Absent/null renders nothing rather than a guess: an older clinic-service
   * omits it, and a standalone mount has no deal context to read it from.
   */
  consentQuestionSetVersion?: number | null;
}) {
  const [scr, setScr] = useState<ScrPanelState>({ kind: 'loading' });
  const [review, setReview] = useState<Loadable<AssessmentReview>>({ state: 'loading', data: null });
  const [prescriptions, setPrescriptions] = useState<Loadable<StaffPrescriptionsResponse>>({ state: 'loading', data: null });
  /**
   * The clinician has deliberately asked to prescribe AGAIN on a case that
   * already has one. Off by default — see the issued panel below for why the
   * escape hatch has to exist at all.
   */
  const [reissuing, setReissuing] = useState(false);
  /**
   * ⚠️ TEMPORARY — the v1 backlog's re-prescribe modal is open.
   *
   * Deleted with `RePrescribeModal` itself; see prescribingFlags.tsx for what
   * else goes in that sweep.
   */
  const [rePrescribing, setRePrescribing] = useState(false);
  /**
   * A prescription has been issued FROM THIS SCREEN since the order was read.
   *
   * The prescribing flags ride on the `order` prop, which the drawer fetched
   * once — so the instant a clinician finishes the job, the server's answer on
   * this screen is stale by exactly one action. Without this latch the card
   * would keep wearing "Prescription owed" underneath a green "Prescription
   * issued" banner, which is the kind of disagreement that teaches people to
   * stop believing the flag. It only ever CLEARS a flag; nothing here can raise
   * one the server did not send.
   */
  const [prescribedHere, setPrescribedHere] = useState(false);
  const [currentState, setCurrentState] = useState<string | null>(order?.state ?? null);
  const [action, setAction] = useState<string | null>(null);
  const [notice, setNotice] = useState<ActionNotice | null>(recoveredNotice ?? null);
  const noticeRef = useRef<HTMLDivElement>(null);
  // The decision awaiting confirmation — its ConfirmDialog closes on confirm,
  // then the action runs (the existing post-action Alert pattern is unchanged).
  const [pending, setPending] = useState<PendingDecision | null>(null);
  const { me } = useAuth();

  useEffect(() => {
    if (recoveredNotice) setNotice(recoveredNotice);
  }, [recoveredNotice]);
  useEffect(() => {
    if (notice) noticeRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [notice]);

  // The review is fetched by assessment id — available WITHOUT an order case, so
  // a submitted assessment gets its full clinical review before checkout (item 2,
  // 2026-07-28). The decision actions below stay gated on the order case.
  const reviewAssessmentId = order?.assessmentRef ?? assessmentId ?? null;
  const loadReview = useCallback(() => {
    if (!reviewAssessmentId) {
      setReview({ state: 'skip', data: null });
      return;
    }
    setReview({ state: 'loading', data: null });
    getAssessmentReview(reviewAssessmentId)
      .then((data) => setReview({ state: 'ready', data }))
      .catch(() => setReview({ state: 'error', data: null }));
  }, [reviewAssessmentId]);

  // SCR quick-copy needs the LINKED patient id, which comes from the order. In
  // review-only mode (no order) there is no linked account yet — the Spine check
  // has nothing to run against — so the whole block is hidden below and no SCR
  // fetch is issued (null → loadScr no-ops).
  //
  // A failed read is TYPED (scrPanelState.ts, 2026-09-25): a patient with no
  // account yet, or with an unconfirmed email, gets the CTO's plain
  // explanation instead of an error, and Try again is offered only when a
  // retry can succeed.
  const scrPatientUserId = order ? order.patientUserId : null;
  const loadScr = useCallback(() => {
    if (!scrPatientUserId) {
      setScr({ kind: 'skip' });
      return;
    }
    setScr({ kind: 'loading' });
    getScr(scrPatientUserId)
      // An empty 2xx keeps the old `!scr.data` guard: the calm no-record line,
      // never a quick-copy card built from nothing.
      .then((data) => setScr(data ? { kind: 'ready', scr: data } : { kind: 'no_record' }))
      .catch((error: unknown) => setScr(scrStateFromError(error, scrPatientUserId)));
  }, [scrPatientUserId]);

  // KEY ALERTS — the double-prescribing check (2026-08-07). The read lives at a
  // VIEW (never in the banner, so a re-render of the leaf can never refire it):
  // here on a standalone mount, or in the deal drawer above us, which needs the
  // same answer to flag its Review tab before the tab is opened. Passing null as
  // the patient id when the answer is hoisted keeps the hook unconditional and
  // issues no second request.
  //
  // Either way the state is the EXPLICIT MedicationSafetyView union, never
  // `MedicationSafety | null`: "not checked yet", "no linked patient so no
  // check applies" and "the check failed" are three different situations and
  // only the first two render nothing. A single null shared between them is the
  // silent-empty failure this whole feature exists to prevent — see the type's
  // note in KeyAlerts.tsx.
  const ownSafety = useMedicationSafety(
    hoistedSafety ? null : (order?.patientUserId ?? null),
    order?.orderCaseId,
  );
  const safety = hoistedSafety ?? ownSafety.view;
  const retrySafety = onRetrySafety ?? ownSafety.retry;

  // Prescription history + the decision actions exist ONLY with an order case.
  const orderCaseId = order?.orderCaseId ?? null;
  const loadPrescriptions = useCallback(async (): Promise<StaffPrescriptionsResponse | null> => {
    if (!orderCaseId) {
      setPrescriptions({ state: 'skip', data: null });
      return null;
    }
    setPrescriptions({ state: 'loading', data: null });
    try {
      const data = await getCasePrescriptions(orderCaseId);
      setPrescriptions({ state: 'ready', data });
      return data;
    } catch {
      setPrescriptions({ state: 'error', data: null });
      return null;
    }
  }, [orderCaseId]);

  // PHOTOS-REVIEWED GATE (2026-08-27) — read at the VIEW like every other
  // state here, because TWO controls key on it: the attestation button inside
  // the weight-photo card and the approve control in the Decision card. One
  // read, one answer, so the two can never disagree about whether the gate is
  // still standing. Money-free by shape, so it is safe on this surface.
  const [photoReview, setPhotoReview] = useState<Loadable<PhotoReviewStatus>>({ state: 'loading', data: null });
  const loadPhotoReview = useCallback(() => {
    if (!orderCaseId) {
      setPhotoReview({ state: 'skip', data: null });
      return;
    }
    setPhotoReview({ state: 'loading', data: null });
    getPhotoReviewStatus(orderCaseId)
      .then((data) => setPhotoReview({ state: 'ready', data }))
      .catch(() => setPhotoReview({ state: 'error', data: null }));
  }, [orderCaseId]);

  useEffect(() => {
    loadReview();
  }, [loadReview]);
  useEffect(() => {
    loadScr();
  }, [loadScr]);
  useEffect(() => {
    loadPrescriptions();
  }, [loadPrescriptions]);
  useEffect(() => {
    loadPhotoReview();
  }, [loadPhotoReview]);

  const isApproved = currentState === 'approved';
  const isRejected = currentState === 'rejected';

  /**
   * ⚠️ THE DECISION CARD'S POST-APPROVAL BRANCH — AND IT IS NOT `isApproved`.
   *
   * This used to be `isApproved`, i.e. `state === 'approved'` EXACTLY, and that
   * single-value test is the reason the v1 backlog's repair control was
   * invisible on every card it was built for. The deleted auto-issue minted the
   * doseless prescription AND emailed the pharmacy inside one approve click, so
   * those orders were at `awaiting_dispense` or beyond within seconds — and the
   * button that fixes them rendered on none of them. Two real patients are at a
   * pharmacy with the wrong strength on a signed document.
   *
   * orders-service deliberately allows the whole post-approval tail (its own
   * words: "they can only correct what they can see"), so this asks the SAME
   * question it does, from the one mirror of its list — see prescribeStates.ts
   * for why that is a mirror rather than a read, and which server file it
   * tracks. `isApproved` survives for the state BADGE, which is describing the
   * literal value and should not be widened.
   *
   * It also closes a second hole in the old shape: a case at `awaiting_dispense`
   * matched neither `isRejected` nor `isApproved`, so it fell all the way
   * through to `FormActions` — Approve and Reject buttons, offered on an order
   * that was already with the pharmacy.
   */
  const isPostApprovalDecision = isClinicianPrescribeState(currentState);
  // 2026-07-21 CRM redesign: approve/reject/prescribe are CLINICIAN-only
  // server-side — an admin viewer gets the disabled mirror, never a 403.
  const isAdminViewer = me?.role === 'ADMIN';

  /**
   * WHO MAY RETRACT STORED EVIDENCE — its own flag, NOT `isAdminViewer`.
   *
   * clinic-service widened `DELETE :id/photos/:photoId` to
   * `@Roles('ADMIN', 'CLINICIAN')` on 2026-09-06 (CTO, over a raised objection —
   * the reasoning on both sides is on that controller). This mirrors it.
   *
   * ⚠️ IT IS A SEPARATE CONSTANT ON PURPOSE. `isAdminViewer` also gates the
   * attestation-name lookup, which resolves an opaque identity ref through an
   * ADMIN-only endpoint — widening that one flag to admit clinicians would have
   * a CLINICIAN issuing a request that can only ever 403, to learn a name R19
   * says they should not be asking for. Two different questions, two flags.
   *
   * R41 all the same: the server is the control. This only decides whether the
   * affordance is drawn.
   */
  const canRetractEvidence = me?.role === 'ADMIN' || me?.role === 'CLINICIAN';

  // WHO attested, resolved to a DISPLAY NAME for an ADMIN viewer only.
  //
  // `photosReviewedBy` is an OPAQUE identity ref on the wire (R19 — the server
  // never sends a name), and the lookup that resolves it is ADMIN-only, so a
  // CLINICIAN viewer must never even issue the request: they see the ROLE
  // instead. That is the SAME rule the deal drawer's activity feed follows for
  // its actor names. A failed lookup falls back to the role too — never an
  // error state, because the stamp itself is still true.
  const [attestedByName, setAttestedByName] = useState<string | null>(null);
  const attestedBy = photoReview.state === 'ready' ? (photoReview.data?.photosReviewedBy ?? null) : null;
  useEffect(() => {
    if (!isAdminViewer || !attestedBy) {
      setAttestedByName(null);
      return;
    }
    let live = true;
    lookupStaffUsers([attestedBy])
      .then(({ users }) => {
        if (!live) return;
        const found = users.find((user) => user.userId === attestedBy);
        setAttestedByName(found ? (found.fullName?.trim() || found.email) : null);
      })
      .catch(() => {
        if (live) setAttestedByName(null);
      });
    return () => {
      live = false;
    };
  }, [isAdminViewer, attestedBy]);

  // Does the approve control have to wait for the attestation?
  //
  // ONLY on a READY status: a failed or in-flight read must never disable
  // approve — that would be the UI inventing a block the server never asked
  // for. R41 cuts both ways here, and the server 409s either way.
  //
  // NOTE the read this DOES NOT touch: the clinic-service review. This block is
  // decided entirely by orders-service, which is why WeightPhotoSection's
  // attestation must render on a failed review — the two services fail
  // independently, and a block from one may only ever be cleared by a control
  // the other one drives.
  const photosGateBlocks =
    photoReview.state === 'ready' &&
    photoReview.data?.required === true &&
    photoReview.data.photosReviewedAt === null;

  // Patient NAME (2026-07-24) from the review's Lead-resolved acquisition
  // identity — null until the review loads or when no name was captured.
  const patientName =
    review.state === 'ready' ? formatPatientName(review.data?.patientIdentity) : null;

  /**
   * Run a decision action behind the shared notice pattern.
   *
   * `explain` lets ONE action opt into the server's own sentence instead of the
   * house line — see `approve` below. It is opt-in rather than the default
   * because most failures on this surface are statuses with no readable body,
   * and a caller has to have decided that THIS route's refusal bodies are
   * PHI-free before it can be shown to anyone (R19).
   */
  async function run(name: string, fn: () => Promise<void>, explain?: (err: unknown) => string) {
    setAction(name);
    setNotice(null);
    try {
      await fn();
    } catch (err: unknown) {
      setNotice({ tone: 'danger', text: explain ? explain(err) : ACTION_FAILED_NOTICE });
    } finally {
      setAction(null);
    }
  }

  function publishNotice(next: ActionNotice) {
    setNotice(next);
    onActioned?.(next);
  }

  const approve = () =>
    run(
      'approve',
      async () => {
        if (!orderCaseId) return;
        await approveOrder(orderCaseId);
        setCurrentState('approved');
        // V1.5: capture auto-issues the month-1 Rx before approve returns, and
        // the deal moves to Won. Read the history so we can tell the clinician
        // the prescription was issued rather than prompting them to issue again.
        const history = await loadPrescriptions();
        publishNotice(
          history && history.items.length > 0
            // FALSE, not true: the history proves a prescription EXISTS, and says
            // nothing about whether the pharmacy received it. See the note on
            // prescriptionIssuedNotice — approve never observes the send.
            ? prescriptionIssuedNotice(false)
            : { tone: 'success', text: 'Order approved. You can now issue the prescription.' },
        );
      },
      // The photos-reviewed 409 says exactly what to do next; the house line
      // does not. See serverSentenceOr — R41's other half: when the server
      // refuses, the clinician reads the SERVER'S reason, not ours.
      (err) => serverSentenceOr(err, ACTION_FAILED_NOTICE),
    );

  const reject = (reason: string) =>
    run('reject', async () => {
      if (!orderCaseId) return;
      await rejectOrder(orderCaseId, reason);
      setCurrentState('rejected');
      publishNotice({ tone: 'success', text: 'Order rejected and the hold released.' });
    });

  /**
   * A prescription already exists for this case. ONE definition, asked by both
   * the "issue it below" banner and the form gate, so the two can never
   * disagree about whether there is work left to do.
   *
   * FALSE while the history is loading or errored — deliberately. An unknown
   * answer must not block a clinician, and it must not claim the work is done.
   */
  const alreadyPrescribed = prescriptions.state === 'ready' && (prescriptions.data?.items.length ?? 0) > 0;

  /**
   * WHAT THIS ORDER STILL OWES A PRESCRIBER — the server's own answer, relayed.
   *
   * NOT derived from `prescriptions` above, deliberately. The same question is
   * answered in orders-service from the predicate that also writes the stored
   * `correctlyPrescribed` permission (the one that stops a pharmacy send), so
   * reading it off the wire is what keeps this screen and the gate saying the
   * same thing. A browser-side re-derivation would be a second predicate, free
   * to drift — which is exactly how the deleted correction panel ended up
   * offering a control on orders the server refused and hiding from orders it
   * blocked.
   */
  /** The newest prescription for this case — the document the temporary
   *  re-prescribe modal starts from (its directions and quantity carry over so
   *  a clinician's own words are not silently blanked). */
  const latestPrescription = prescriptions.state === 'ready' ? (prescriptions.data?.items[0] ?? null) : null;

  /**
   * WHAT THE RE-SAVE MODAL IS GIVEN — THE READ'S STATE, NOT ITS CONTENTS (2026-09-13).
   *
   * ⚠️ THIS IS THE DEAD-END FIX. The modal used to be handed
   * `latestPrescription?.items ?? []`, which flattens THREE different situations
   * into one empty array: the history still in flight, the history errored, and a
   * document with no lines on it. The modal seeds its editors in a one-shot
   * initializer, so every one of those opened a dialog with zero strength
   * pickers, zero Directions fields and a Continue that could never enable —
   * with nothing on screen saying why. The control is gated on the ORDER's
   * `needsPrescriptionReapproval` while its contents come from this SEPARATE
   * read, so the two disagreeing is not an edge case, it is a race that runs on
   * every open (I1 — an absent value is visibly flagged, never blanked).
   *
   * Relaying the state instead lets the modal say which of the three it is and
   * offer a retry for the one a retry fixes. `skip` (no order case) lands on
   * `error` deliberately: there is no history to read and no lines to correct,
   * which is the same thing the clinician needs told — and the branch this feeds
   * cannot render without an order anyway.
   */
  const represcribeSource: ReprescribeSource =
    prescriptions.state === 'loading'
      ? { state: 'loading' }
      : prescriptions.state === 'ready' && latestPrescription
        ? {
            state: 'ready',
            // `items` is optional on the wire — an older orders build omits it.
            // An absent list is an EMPTY ready, which the modal answers with its
            // own sentence, not with a form nobody can finish.
            items: latestPrescription.items ?? [],
            repeatFlag: latestPrescription.repeatFlag,
          }
        : { state: 'error' };

  const flags: PrescribingFlags = prescribedHere
    ? { awaitingPrescription: false, needsPrescriptionReapproval: false }
    : prescribingFlagsOf(order);

  const issuePrescription = (body: PrescriptionIssueRequest) =>
    run(
      'prescribe',
      async () => {
        if (!orderCaseId) return;
        const result = await prescribe(orderCaseId, body);
        publishNotice(prescriptionIssuedNotice(result.pharmacySent));
        // The order row this screen was handed is now one action out of date —
        // see `prescribedHere`. Latched here rather than in each caller so the
        // ordinary form and the temporary modal can never disagree about it.
        setPrescribedHere(true);
        setRePrescribing(false);
        // Refetch so the newly-issued prescription appears in the history below immediately.
        await loadPrescriptions();
        // A successful issue means the form has done its job — fall back to the
        // issued state rather than leaving a live form under a success banner.
        setReissuing(false);
      },
      // Every refusal on this route is a STATE refusal that names itself, and
      // the house line told a clinician to retry an action that cannot succeed.
      // Same reasoning as approve above — R41's other half.
      (err) => serverSentenceOr(err, ACTION_FAILED_NOTICE),
    );

  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        style={{ background: 'none', border: 0, color: 'var(--gps-link)', font: 'inherit', fontSize: '14px', cursor: 'pointer', textDecoration: 'underline', marginBottom: 'var(--gps-s-3)', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
      >
        <ChevronLeftIcon size={14} />
        Back
      </button>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--gps-s-3)', flexWrap: 'wrap', marginBottom: 'var(--gps-s-2)' }}>
        <h1 style={{ fontFamily: 'var(--gps-font-display)', fontSize: '24px', margin: 0, color: 'var(--gps-text)' }}>Case review</h1>
        {order ? <Badge tone="muted">{LANE_LABEL[order.lane] ?? order.lane}</Badge> : null}
        {currentState ? (
          <Badge tone={isApproved ? 'positive' : isRejected ? 'muted' : 'pending'}>{currentState}</Badge>
        ) : (
          <Badge tone="muted">Submitted — no order yet</Badge>
        )}
        {/* WHAT IS STILL OWED, in the existing Badge vocabulary — beside the
            state, because "approved" on its own now means the money is taken
            and the medicine has NOT been prescribed, and a reader who knows the
            old meaning will otherwise read it as finished. */}
        <PrescribingFlagChip flags={flags} />
        {/* Provenance (v0.6.5, moved ADMIN→CLINICIAN 2026-07-20): a clinician
            must see WHO filled the form — a clinician-initiated form (filled
            by staff during a call) gets the attention tone; a patient form is
            the unremarkable default. An absent `origin` (older assessments)
            reads as patient-filled. */}
        {review.state === 'ready' && review.data ? (
          review.data.origin === 'clinician' ? (
            <Badge tone="pending">Clinician-initiated form</Badge>
          ) : (
            <Badge tone="muted">Patient form</Badge>
          )
        ) : null}
      </div>
      {/* Patient name (2026-07-24) — from the review's Lead-resolved identity;
          "Not recorded" once the review has loaded but no name was captured. */}
      {review.state === 'ready' ? (
        <p style={{ fontSize: '15px', margin: '0 0 var(--gps-s-1)', color: 'var(--gps-text)' }}>
          Patient:{' '}
          <strong style={{ fontWeight: 600 }}>
            {patientName ?? <span style={{ fontWeight: 400, color: 'var(--gps-muted)' }}>Not recorded</span>}
          </strong>
        </p>
      ) : null}
      {order ? <Help>Order reference {orderDisplayRef(order)}</Help> : null}

      {/* Review-only (no order, so no Decision card): keep the notice at the
          top. Order-backed reviews render it in the Decision card instead —
          that is where the clinician just acted, so it is on screen. role=
          "status" (not "alert") so the R31 crisis banner below is the ONLY
          assertive live region on this surface — two assertive regions fight
          each other in a screen reader, and the safeguarding banner must win.
          The same rule binds the key-alerts bands below (see KeyAlerts.tsx). */}
      {!order && notice ? (
        <Alert tone={notice.tone} role="status" style={{ marginBottom: 'var(--gps-s-4)' }}>
          {notice.text}
        </Alert>
      ) : null}

      {/* R31 — mental-health safeguarding banner, pinned above everything the
          reviewer reads. Driven by the server's authoritative `mhRisk` flag.
          Deliberately NOT order-gated: the cohort this protects is the one that
          hard-stops in the questionnaire and therefore never gets an order case,
          so it must render in review-only mode too. */}
      {review.state === 'ready' && crisisSignal(review.data).show ? (
        <CrisisBanner category={crisisSignal(review.data).category} />
      ) : null}

      {/* KEY ALERTS — the double-prescribing check, directly under the R31
          crisis banner and above the identity block the danger copy points at.
          Renders for BOTH viewers: an admin building a plan in the back office
          needs this exactly as much as the prescribing clinician, so it is
          deliberately NOT behind `isAdminViewer`. It NEVER gates the decision
          actions below — legitimate switches exist, so this alerts and the
          clinician decides. The section renders nothing while idle and nothing
          when there is no linked patient to check; a FAILED check warns. */}
      <KeyAlertsSection view={safety} onRetry={retrySafety} />

      {/* WAS THE PATIENT TOLD WHAT THIS MEDICINE CAN DO TO THEM? (Reneal Patel,
          2026-08-18.) Directly under the key alerts and well above the Decision
          card, because it is a thing to read BEFORE approving, not a receipt to
          check afterwards. ORDER-GATED: the acknowledgement is a fact about a
          paid order, so a review-only assessment with no order has nothing to
          say here — and an "unacknowledged" band on a case that has not reached
          checkout would be noise on every submitted assessment. The raw value
          is passed straight through: SafetyAckBand tells recorded, never
          acknowledged and unknown apart, and normalising it here would throw
          that away. */}
      {order ? <SafetyAckBand ack={order.safetyAck} /> : null}

      {/* Answers changed after an earlier check ended not-eligible. Directly
          under the key alerts, in the SAME visual language (Alert + words, never
          colour alone). role="status", NOT "alert" — the R31 crisis banner above
          keeps this surface's only assertive region. Renders nothing when there
          is nothing to say. */}
      <PriorStopsBand
        stops={review.state === 'ready' ? (review.data?.priorStops ?? []) : []}
        sections={review.state === 'ready' ? (review.data?.sections ?? []) : []}
      />

      {/* SCR quick-copy — pinned at the top. ORDER-ONLY: hidden in review-only mode,
          where there is no linked patient account for the Spine check to run against
          (item 2). Showing it order-less renders an empty card that reads as broken.
          Every state, including the no-account explanations, is ScrPanel's. */}
      {order ? (
        <div style={{ marginBottom: 'var(--gps-s-4)' }}>
          <ScrPanel
            state={scr}
            onRetry={loadScr}
            name={review.data?.patientIdentity ?? undefined}
            consent={review.data?.scrCheckConsent ?? undefined}
          />
        </div>
      ) : null}

      <div style={{ display: 'grid', gap: 'var(--gps-s-4)', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
        {/* Clinical summary + weight photo + structured questionnaire. */}
        <div style={{ display: 'grid', gap: 'var(--gps-s-4)', gridAutoRows: 'min-content' }}>
          <ClinicalSummary review={review} onRetry={loadReview} />
          {/* WHAT THE PATIENT ATTESTED ABOUT THEIR WEIGHT (clinic #166) — ABOVE
              the photo card, because from question set v3 the declaration is
              the primary evidence and the photo is the verification a clinician
              may CHOOSE to ask for. Reading them the other way round invites
              the old assumption that the image is the baseline and the
              declaration a footnote, which is the assumption this change
              exists to remove. Renders nothing for a v1/v2 photo patient. */}
          <WeightDeclarationSection review={review} agreedAt={consentAgreedAt} questionSetVersion={consentQuestionSetVersion} />
          <WeightPhotoSection
            review={review}
            assessmentId={reviewAssessmentId}
            orderCaseId={orderCaseId}
            isAdminViewer={isAdminViewer}
            canRetractEvidence={canRetractEvidence}
            photoReview={photoReview}
            attestedByName={attestedByName}
            onAttested={(status) => setPhotoReview({ state: 'ready', data: status })}
            onReloadReview={loadReview}
          />
          <StructuredQuestionnaire review={review} onRetry={loadReview} consentAgreedAt={consentAgreedAt} />
        </div>

        {/* Delivery + decision actions + prescription history exist ONLY with an
            order case. An order-less submitted assessment shows just the clinical
            review (left column) + a note that decisions unlock at checkout (item 2). */}
        {order ? (
        <div style={{ display: 'grid', gap: 'var(--gps-s-4)', gridAutoRows: 'min-content' }}>
          {/* WHAT THE PATIENT CHOSE, ABOVE THE DECISION THAT ACTS ON IT (CTO,
              2026-09-13). Until now the medication and term lived in a single
              `Help` line inside `PrescribeForm` — which mounts only AFTER
              approval — so the clinician approved or rejected without ever
              being shown what they were approving.

              ⚠️ NOT RENDERED POST-APPROVAL. Once `isPostApprovalDecision` is
              true the Decision card below contains `PrescribeForm`, which shows
              the same plan beside its prefill. Two identical plan lines in one
              column reads as two different facts; the earlier surface is the one
              that was missing, so this fills that gap and stops. */}
          {!isPostApprovalDecision ? (
            <PatientPlanCard
              clinicSlug={order.clinicSlug ?? DEFAULT_CLINIC_SLUG}
              productKey={order.productKey}
              months={order.months}
            />
          ) : null}

          <Card>
            <SectionTitle>Delivery address</SectionTitle>
            {order.deliveryAddress ? (
              <p style={{ color: 'var(--gps-text)', fontSize: '14.5px', margin: 0 }}>
                {formatAddress(order.deliveryAddress)}
              </p>
            ) : (
              <Help style={{ marginBottom: 0 }}>No delivery address on file.</Help>
            )}
            <p style={{ color: 'var(--gps-muted)', fontSize: '12px', margin: 'var(--gps-s-2) 0 0' }}>Read-only — the patient owns this until approval.</p>
          </Card>

          <Card>
            <SectionTitle>Decision</SectionTitle>
            {notice ? (
              <div ref={noticeRef}>
                <Alert tone={notice.tone} role="status" style={{ marginBottom: 'var(--gps-s-3)' }}>
                  {notice.text}
                </Alert>
              </div>
            ) : null}
            {isRejected ? (
              <Help style={{ marginBottom: 0 }}>This case has been rejected.</Help>
            ) : isPostApprovalDecision ? (
              <>
                {/* ⚠️ THE REASON, IN WORDS, ABOVE THE CONTROLS — and it REPLACES
                    the old "Approved — issue the prescription below" line rather
                    than joining it.

                    That line was written when approval auto-issued the month-1
                    prescription, so it described a courtesy. Since 2026-09-12 it
                    describes the only thing standing between a paying patient
                    and their medicine, and a green success alert is the wrong
                    voice for that. `PrescribingReasonNotice` says what has
                    happened (approved, paid), what has not (nothing is with the
                    pharmacy) and what to do — and it renders for an ADMIN too,
                    who cannot prescribe but must be able to see why an order has
                    stopped. */}
                <PrescribingReasonNotice flags={flags} style={{ marginBottom: 'var(--gps-s-3)' }} />
                {/* `isApproved`, not the widened branch: this sentence says the
                    word "Approved", and on an order that is already at
                    `dispensed` it would be describing a stage the case left days
                    ago. The widened branch is about WHO MAY PRESCRIBE; this line
                    is about where the case actually is. */}
                {isApproved && !notice && !alreadyPrescribed && prescriptions.state === 'ready' && !flags.awaitingPrescription ? (
                  <Alert tone="success" style={{ marginBottom: 'var(--gps-s-3)' }}>Approved — issue the prescription below.</Alert>
                ) : null}
                {isAdminViewer ? (
                  // 2026-07-21 CRM: prescribing is CLINICIAN-only server-side —
                  // mirror it rather than render a form that can only 403.
                  <Help style={{ marginBottom: 0 }}>Prescribing is a clinician action.</Help>
                ) : flags.needsPrescriptionReapproval ? (
                  /* ⚠️ TEMPORARY — THE V1 BACKLOG BRANCH, AND IT MUST RUN BEFORE
                     THE `alreadyPrescribed` ONE.
                     These orders HAVE a prescription, so the branch below would
                     tell the clinician "this supply has already been prescribed
                     — no further action is needed" about the exact document that
                     records no strength and that the pharmacy gate is refusing.
                     Deleted with the modal; see prescribingFlags.tsx.

                     ⚠️ GATED ON THE SERVER'S FLAG ALONE, NOT ON A STATE. The
                     flag is computed by orders-service's `prescribingStatus()`,
                     which returns false outright for any state outside
                     `CLINICIAN_PRESCRIBE_STATES` — so the state set is already
                     folded into it and re-asking it here would be a second,
                     driftable copy of the same rule (R41: relay, do not
                     re-derive). The branch above is the only place a state is
                     consulted, and only to choose which SURFACE to render. */
                  <>
                    <Button
                      type="button"
                      variant="cta"
                      onClick={() => setRePrescribing(true)}
                      style={{ width: 'auto', minHeight: '44px', padding: '10px 18px' }}
                    >
                      Re-save the prescription
                    </Button>
                    {rePrescribing ? (
                      <RePrescribeModal
                        orderCaseId={order.orderCaseId}
                        productKey={order.productKey}
                        orderState={currentState}
                        source={represcribeSource}
                        // The read behind the modal, re-run. ⚠️ THE BUTTON ABOVE
                        // IS DELIBERATELY NOT DISABLED WHILE THAT READ IS IN
                        // FLIGHT: a disabled control cannot offer a retry, and
                        // the failed read is precisely the case a clinician
                        // needs one for. The dialog carries the honest state
                        // instead, which also covers the read failing while it
                        // is already open.
                        onRetry={() => void loadPrescriptions()}
                        // The SERVER'S send fact, not an assumption — see the
                        // note on RePrescribeModal's `onDone`.
                        onDone={(pharmacySent) => {
                          setRePrescribing(false);
                          setPrescribedHere(true);
                          publishNotice(prescriptionIssuedNotice(pharmacySent));
                          void loadPrescriptions();
                        }}
                        onCancel={() => setRePrescribing(false)}
                      />
                    ) : null}
                  </>
                ) : alreadyPrescribed && !reissuing ? (
                  /*
                   * ⚠️ DO NOT OFFER AN ACTION THE SERVER HAS ALREADY PERFORMED.
                   * Capture auto-issues the month-1 prescription and sends it to
                   * the pharmacy before approve() even returns, so by the time a
                   * clinician reaches this card the work is usually done. The
                   * form used to render on `isApproved` ALONE — while the very
                   * history that proves a prescription exists was already loaded
                   * one branch above, and used only to pick banner wording. A
                   * clinician on production pressed Prescribe 36 seconds after
                   * the pharmacy had been emailed, was told to try again, and
                   * did.
                   *
                   * CONSERVATIVE BY CONSTRUCTION (R41 — the UI must not invent a
                   * block): `alreadyPrescribed` is false while the history is
                   * loading OR errored, so a failed read leaves the form exactly
                   * where it was. The re-issue escape stays because it is the
                   * ONLY recovery for a prescription whose pharmacy send never
                   * left — this read carries no send state, so the server, which
                   * does, remains the one that decides (and now says so in its
                   * own words).
                   */
                  <>
                    {/* Suppressed when an action notice is already up — the
                        just-issued success line says this better, and two
                        stacked success alerts read as two separate events. */}
                    {!notice ? (
                      <Alert tone="success" role="status" style={{ marginBottom: 'var(--gps-s-3)' }}>
                        This supply has already been prescribed — see the history below. No further action is needed.
                      </Alert>
                    ) : null}
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => setReissuing(true)}
                      style={{ width: 'auto', minHeight: '44px', padding: '10px 18px' }}
                    >
                      Issue another prescription
                    </Button>
                  </>
                ) : (
                  // The builder's medication select is fed by the case's own
                  // clinic; older items minted before clinicSlug shipped fall
                  // back to the default clinic's catalogue.
                  <PrescribeForm
                    busy={action === 'prescribe'}
                    orderCaseId={order.orderCaseId}
                    clinicSlug={order.clinicSlug ?? DEFAULT_CLINIC_SLUG}
                    prefillProductKey={order.productKey}
                    prefillMonths={order.months}
                    onSubmit={(body, summary, supplies) => setPending({ kind: 'prescribe', body, summary, supplies })}
                  />
                )}
              </>
            ) : isAdminViewer ? (
              // 2026-07-21 CRM redesign: approve/reject dropped ADMIN from the
              // server @Roles — the disabled buttons + label mirror the 403,
              // never attempt it ("the UI is never the control", R41).
              <>
                <Alert tone="info" role="status" style={{ marginBottom: 'var(--gps-s-3)' }}>
                  Waiting for clinician&rsquo;s approval
                </Alert>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <Button type="button" variant="cta" disabled style={{ width: 'auto', minHeight: '44px', padding: '10px 18px' }}>
                    Approve
                  </Button>
                  <Button type="button" variant="ghost" disabled style={{ width: 'auto', minHeight: '44px', padding: '10px 18px' }}>
                    Reject
                  </Button>
                </div>
              </>
            ) : (
              <FormActions
                busy={action}
                blockedReason={photosGateBlocks ? APPROVE_PHOTO_GATE_REASON : null}
                onApprove={() => setPending({ kind: 'approve' })}
                onReject={() => setPending({ kind: 'reject' })}
              />
            )}
          </Card>

          {/* 2026-08-27 restructure — the ASSESSMENT notes thread, moved here
              from the Activity tab: decide and document in one place. Titled
              "Assessment notes" ONLY because the per-case thread below already
              owns "Clinical notes" on this screen (see the header note); the
              store, the composer and the author-only edit rule are unchanged. */}
          {reviewAssessmentId ? (
            <AssessmentNotesSection
              assessmentId={reviewAssessmentId}
              heading="Assessment notes"
              composerLabel="Add an assessment note"
              composerTextboxLabel="New assessment note"
            />
          ) : null}

          <PrescriptionHistory prescriptions={prescriptions} onRetry={loadPrescriptions} />

          {/* Clinical notes — a running note thread for this case, kept
              INDEPENDENT of the prescribe/reject decision (G3). */}
          <CaseNotesSection orderCaseId={order.orderCaseId} />
        </div>
        ) : (
          <div style={{ display: 'grid', gap: 'var(--gps-s-4)', gridAutoRows: 'min-content' }}>
            <Card>
              <SectionTitle>Decision</SectionTitle>
              <Help style={{ marginBottom: 0 }}>
                This assessment has been submitted, but the patient hasn&rsquo;t checked out yet, so there is
                no order to action. Approve, reject and prescribe unlock once an order case exists.
              </Help>
            </Card>
            {/* 2026-08-27 restructure — the same moved thread on the order-less
                review, under its established name: it is the only notes thread
                on this screen, so no disambiguation is needed here. */}
            {reviewAssessmentId ? <AssessmentNotesSection assessmentId={reviewAssessmentId} /> : null}
          </div>
        )}
      </div>

      {/* ── The clinical confirmations (G2). Each dialog closes on confirm and
          the action runs behind the existing `run` notice pattern. ── */}
      {pending?.kind === 'approve' ? (
        <ConfirmDialog
          open
          title="Approve treatment?"
          body="The patient's payment will be captured and the plan activated."
          confirmLabel="Approve"
          onConfirm={() => {
            setPending(null);
            void approve();
          }}
          onCancel={() => setPending(null)}
        />
      ) : null}

      {pending?.kind === 'reject' ? (
        <ConfirmDialog
          open
          title="Reject this order?"
          tone="danger"
          body="The payment hold will be released and the case closed."
          requireReason
          reasonLabel="Reason for rejection (recorded in the audit log)"
          confirmLabel="Reject order"
          onConfirm={(reason) => {
            setPending(null);
            void reject(reason ?? '');
          }}
          onCancel={() => setPending(null)}
        />
      ) : null}

      {pending?.kind === 'prescribe' ? (
        <ConfirmDialog
          open
          title="Issue prescription?"
          body={
            <>
              <p style={{ margin: 0 }}>
                {`Issue prescription: ${summariseForConfirm(pending.body.items, pending.summary)}? It will be sent to the pharmacy.`}
              </p>
              {/* THE SUPPLIES, on the last screen before the document is signed
                  — the incident this section exists for was a supply that never
                  went out, so the confirmation says what will go out with it,
                  and says so in words when nothing will. */}
              {pending.supplies.length > 0 ? (
                <div style={{ marginTop: '10px' }}>
                  <p style={{ margin: '0 0 4px', fontWeight: 600 }}>Supplies to include:</p>
                  <SupplyLines supplies={pending.supplies} label="Supplies to include" />
                </div>
              ) : (
                <p style={{ margin: '10px 0 0' }}>{NO_SUPPLIES_COPY}</p>
              )}
            </>
          }
          confirmLabel="Issue prescription"
          onConfirm={() => {
            const body = pending.body;
            setPending(null);
            void issuePrescription(body);
          }}
          onCancel={() => setPending(null)}
        />
      ) : null}

    </div>
  );
}

/**
 * Pending decision actions: Approve (the single terracotta CTA) + Reject.
 *
 * `blockedReason` (2026-08-27) is the photos-reviewed gate's AFFORDANCE: when
 * given, approve is disabled and the reason is rendered in TEXT tied to the
 * button by `aria-describedby` — never colour alone, never a bare `disabled`
 * with nothing to read (WCAG 2.1 AA 1.4.1 / 3.3.1), the same structure the
 * checkout pay gate uses. R41: the server still refuses an unattested approve;
 * this only means the clinician learns why from the screen rather than from a
 * failed action.
 *
 * REJECT IS NEVER BLOCKED BY IT. Rejecting releases the hold and closes the
 * case — a patient who has not uploaded a photo is exactly someone a clinician
 * may need to turn away, so gating that too would trap the case.
 */
function FormActions({
  busy,
  blockedReason = null,
  onApprove,
  onReject,
}: {
  busy: string | null;
  blockedReason?: string | null;
  onApprove: () => void;
  onReject: () => void;
}) {
  return (
    <div style={{ display: 'grid', gap: 'var(--gps-s-3)' }}>
      <Help style={{ marginBottom: 0 }}>Approve to capture the first month and unlock prescribing, or reject to release the hold.</Help>
      <Button
        type="button"
        variant="cta"
        disabled={busy !== null || blockedReason !== null}
        aria-describedby={blockedReason ? APPROVE_BLOCKED_REASON_ID : undefined}
        onClick={onApprove}
      >
        {busy === 'approve' ? 'Approving…' : 'Approve'}
      </Button>
      {blockedReason ? (
        <p id={APPROVE_BLOCKED_REASON_ID} style={{ fontSize: '13px', color: 'var(--gps-muted)', margin: 0, lineHeight: 1.45 }}>
          {blockedReason}
        </p>
      ) : null}
      <Button type="button" variant="ghost" disabled={busy !== null} onClick={onReject}>
        {busy === 'reject' ? 'Rejecting…' : 'Reject'}
      </Button>
    </div>
  );
}

/** BMI, thresholds, soft-stop flags and prior treatment — the clinical at-a-glance. */
/** Friendly labels for the coarse hard-stop band (GDPR-3 — never the raw stopKey). */
const MH_CATEGORY_LABEL: Record<MhRiskCategory, string> = {
  safety_condition: 'safety condition',
  bmi_floor: 'BMI floor',
  bmi_gate: 'BMI gate',
  age: 'age',
  pregnancy: 'pregnancy',
  clinical: 'clinical',
};

/**
 * R31 mental-health safeguarding banner. Shown at the top of the staff review
 * whenever the server flags `mhRisk`. Carries the coarse category only (never the
 * precise stopKey) + the canonical crisis signposting (999 / Samaritans / NHS 111).
 */
/**
 * Should the R31 crisis banner show, and under which coarse band?
 *
 * THE OR IS THE POINT. `mhRisk` is a PER-ROW value, so a patient who was
 * hard-stopped on a mental-health answer, pressed Back, and changed that answer
 * produces a CURRENT row with `mhRisk: false` — and the banner would silently
 * disappear for exactly the person R31 exists to protect. Any prior stop that
 * carried the flag keeps it lit.
 *
 * The category prefers the current row's band, falling back to the flagged hop's
 * — so a resumed journey still names a band rather than degrading to "unknown".
 */
export function crisisSignal(
  review: Pick<AssessmentReview, 'mhRisk' | 'mhRiskCategory' | 'priorStops'> | null | undefined,
): { show: boolean; category: MhRiskCategory | null } {
  if (!review) return { show: false, category: null };
  const flaggedHop = (review.priorStops ?? []).find((s) => s.mhRisk);
  const show = review.mhRisk === true || flaggedHop !== undefined;
  if (!show) return { show: false, category: null };
  return { show: true, category: review.mhRiskCategory ?? flaggedHop?.category ?? null };
}

/** Long-form UK date for staff copy — "6 Aug 2026". */
function stopDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? 'an earlier date'
    : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** The displayed prompt for a question key, from what is actually on screen. */
function promptFor(sections: AssessmentReview['sections'], questionKey: string | null): string | null {
  if (!questionKey) return null;
  for (const section of sections) {
    const item = section.items.find((i) => i.questionKey === questionKey);
    if (item) return item.prompt;
  }
  return null;
}

/**
 * "Answers changed after an earlier check." One line per superseded journey,
 * newest first.
 *
 * The copy states FACTS and draws no clinical inference — a patient may have
 * misread a question the first time, or genuinely changed. It is the clinician's
 * call, so this alerts and never blocks: nothing here disables a control.
 *
 * Three wordings, because the server can be honest about three different things:
 *  - the question is known AND the answer demonstrably differs;
 *  - the question is known but the comparison was not determinable;
 *  - no question is attributable at all (the submit-time lane) — then we say the
 *    weaker, true thing rather than guessing at "the last one you answered".
 */
function PriorStopsBand({
  stops,
  sections,
}: {
  stops: PriorStop[];
  sections: AssessmentReview['sections'];
}) {
  if (stops.length === 0) return null;
  return (
    <Alert tone="warning" role="status" style={{ marginBottom: 'var(--gps-s-4)' }}>
      <strong style={{ display: 'block', marginBottom: 'var(--gps-s-1)' }}>
        Answers changed after an earlier check
      </strong>
      <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
        {stops.map((stop) => {
          const prompt = promptFor(sections, stop.questionKey);
          const when = stopDate(stop.stoppedAt);
          return (
            <li key={stop.assessmentId} style={{ marginBottom: '2px' }}>
              {prompt && stop.answerChanged === true ? (
                <>
                  A previous check on {when} was stopped at &ldquo;{prompt}&rdquo; — the answer given
                  now is different.
                </>
              ) : prompt ? (
                <>A previous check on {when} was stopped at &ldquo;{prompt}&rdquo;.</>
              ) : (
                <>
                  A previous check on {when} was assessed as not eligible; the questionnaire was
                  changed and re-submitted.
                </>
              )}
              {stop.mhRisk ? ' This related to a mental-health question.' : ''}
            </li>
          );
        })}
      </ul>
    </Alert>
  );
}

function CrisisBanner({ category }: { category: MhRiskCategory | null }) {
  // `mhRiskCategory` is an OPEN string on the wire — a band the portal doesn't
  // recognise yet must NEVER render as "(undefined)" on a safeguarding banner, so
  // fall back to no parenthetical. The band (e.g. `safety_condition`) is also not
  // itself the mental-health cause, so it is shown as a note, not asserted as it.
  const label = category ? (MH_CATEGORY_LABEL[category] ?? null) : null;
  return (
    <Alert tone="danger" role="alert" style={{ marginBottom: 'var(--gps-s-4)' }}>
      <strong style={{ display: 'block', marginBottom: 'var(--gps-s-1)' }}>Mental-health safeguarding flag</strong>
      <p style={{ margin: 'var(--gps-s-2) 0' }}>
        This assessment is flagged for mental-health safeguarding
        {label ? ` (${label})` : ''}. Follow your safeguarding process
        before any clinical decision, and share these support options with the patient if appropriate:
      </p>
      <ul style={{ margin: 0, paddingLeft: 'var(--gps-s-4)' }}>
        {CRISIS_LINES.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
    </Alert>
  );
}

function ClinicalSummary({ review, onRetry }: { review: Loadable<AssessmentReview>; onRetry: () => void }) {
  if (review.state === 'loading') return <Card><Spinner label="Loading assessment…" /></Card>;
  if (review.state === 'error') return <Card><ErrorState message="Sorry — we could not load the assessment." onRetry={onRetry} /></Card>;
  if (review.state === 'skip' || !review.data) {
    return (
      <Card>
        <SectionTitle>Clinical summary</SectionTitle>
        <Help style={{ marginBottom: 0 }}>The structured assessment is not linked to this case yet, so the questionnaire and clinical summary are unavailable.</Help>
      </Card>
    );
  }
  const r = review.data;
  return (
    <Card>
      <SectionTitle>Clinical summary</SectionTitle>
      <Row label="Eligibility" value={r.eligibility.replace(/_/g, ' ')} />
      {typeof r.bmi === 'number' ? (
        <Row label="BMI" value={`${r.bmi.toFixed(1)}${typeof r.bmiThreshold === 'number' ? ` (threshold ${r.bmiThreshold.toFixed(1)})` : ''}`} />
      ) : null}
      {r.priorTreatment ? <Row label="Prior treatment" value={r.priorTreatment} /> : null}
      <div style={{ marginTop: 'var(--gps-s-3)' }}>
        <span style={{ display: 'block', fontSize: '12.5px', fontWeight: 600, color: 'var(--gps-muted)', marginBottom: '6px' }}>Soft-stop flags</span>
        {r.flags.length === 0 ? (
          <Badge tone="positive">None raised</Badge>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {r.flags.map((flag) => (
              <Badge key={flag} tone="pending">{flag.replace(/_/g, ' ')}</Badge>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

/**
 * The alt text for one gallery photo. A SINGLE photo keeps the original,
 * unnumbered wording — numbering a set of one is noise for a screen-reader
 * user, and that exact string is what the review's photo coverage names.
 */
/* The alt text moved to photos/PhotoLightbox as `photoAlt` when the viewer
   became shared with the admin gallery (2026-09-02). One copy, so the two staff
   consoles cannot describe the same photo two different ways to a screen reader. */

/**
 * WHAT THE PATIENT DECLARED ABOUT THEIR WEIGHT (clinic #166, 2026-09-04).
 *
 * From question set v3, q14 is not a photo ask — it is a DECLARATION the patient
 * ticks. Reneal Patel approved dropping the upfront image on condition that the
 * self-certification is "robust", and robust means RECONSTRUCTABLE: a prescriber
 * has to be able to see what the patient undertook, what number they undertook
 * it about, when, and against which version of the wording. Those four facts are
 * this card.
 *
 * ⚠️ THE WORDING IS QUOTED, NOT SUMMARISED. `attested` is the option label
 * clinic-service serves from its append-only wording archive, pinned
 * character-for-character in its seed spec. It is the clinical record of an
 * undertaking a patient gave; paraphrasing it here would put words in their
 * mouth on the screen a prescriber signs against. Same for the prompt: it is
 * the readback line THEY were shown, projected from THEIR OWN question set.
 *
 * ⚠️ AND EVERY LINE IS OMITTED RATHER THAN GUESSED. No capture on the review, no
 * weight line. No `consentAgreedAt` from the deal detail, no "when" line. No
 * `consentQuestionSetVersion`, no version line. An absent field means an older
 * clinic-service or a standalone mount, and a "—" against "Wording version"
 * would read as a declaration made against nothing.
 *
 * It renders NOTHING for a v1/v2 photo patient (see `weightAskState`): they were
 * never asked to declare anything, and an empty declaration card on their review
 * would invent an undertaking.
 */
function WeightDeclarationSection({
  review,
  agreedAt,
  questionSetVersion,
}: {
  review: Loadable<AssessmentReview>;
  agreedAt?: string | null;
  questionSetVersion?: number | null;
}) {
  // Nothing while the review is in flight or failed: ClinicalSummary above owns
  // that state, and a second copy of it is noise. Never a placeholder card —
  // this card's presence is itself a claim about which question the patient saw.
  if (review.state !== 'ready' || !review.data) return null;
  const declaration = weightDeclarationView(review.data);
  if (!declaration) return null;
  return (
    <Card>
      <SectionTitle>Weight declaration</SectionTitle>
      {/* The patient's own words, as a quotation. `blockquote` because it IS a
          quotation of somebody else's statement, and the tick glyph is
          decorative — the visible text carries the meaning (WCAG 1.4.1). */}
      <p style={{ margin: '0 0 var(--gps-s-2)', fontSize: '13px', color: 'var(--gps-muted)' }}>
        {declaration.prompt}
      </p>
      <blockquote
        style={{
          margin: '0 0 var(--gps-s-3)',
          padding: '0 0 0 var(--gps-s-3)',
          borderLeft: '3px solid var(--gps-border)',
          color: 'var(--gps-text)',
          fontSize: '14.5px',
          lineHeight: 1.5,
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: '6px' }}>
          <span style={{ color: 'var(--gps-green-text)' }} aria-hidden="true">
            <CheckIcon size={12} />
          </span>
          <span>{declaration.attested}</span>
        </span>
      </blockquote>
      <dl style={{ margin: 0, display: 'grid', gap: '6px' }}>
        {declaration.declaredWeight ? (
          <DeclarationFact
            label="Weight declared"
            /* THE NUMBER THE BMI GATE RAN ON — the server reads the declaration
               back from the q0/q1 capture precisely so the certified weight and
               the assessed weight cannot disagree (see WeightDeclarationView). */
            value={declaration.declaredWeight}
          />
        ) : null}
        {agreedAt ? <DeclarationFact label="Declared" value={formatUtcDateTime(agreedAt)} /> : null}
        {typeof questionSetVersion === 'number' ? (
          <DeclarationFact
            label="Wording version"
            /* The reconstruction key. "v3" and not a bare 3, so it reads as a
               version rather than a count of something. */
            value={`Question set v${questionSetVersion}`}
          />
        ) : null}
      </dl>
    </Card>
  );
}

/** One label/value pair on the declaration card — a real dt/dd, so the label
 *  and the fact stay associated for assistive tech rather than being two spans
 *  that happen to sit next to each other. */
function DeclarationFact({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'baseline' }}>
      <dt style={{ fontSize: '13px', color: 'var(--gps-muted)', minWidth: '9rem' }}>{label}</dt>
      <dd style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: 'var(--gps-text)' }}>{value}</dd>
    </div>
  );
}

/**
 * The submitted weight photos, in their own card near the flags/BMI block —
 * the GALLERY plus the clinician's attestation (CTO + Reneal, 2026-08-27).
 *
 * THE STATES, mirroring SafetyAckBand's rule that absence is shown, never
 * implied:
 *   one or more signed urls — the thumbnail grid; each enlarges in the shared
 *                       DialogShell (the existing dialog family — no lightbox
 *                       dependency, and the modal a11y is already solved there);
 *   photo on file, no url — a photo IS on file but this environment returned no
 *                       preview (stub-mode object storage): a plain note, not a
 *                       broken image;
 *   NONE              — absence, always SHOWN, in one of THREE readings — see
 *                       the block below. The old silent render-nothing here is
 *                       what let the one clinician decision that needs a photo
 *                       be made on a screen that never admitted it was missing,
 *                       and that stays fixed. `role="status"` (polite), not
 *                       "alert" — the R31 crisis banner owns the single
 *                       assertive region on this surface (see SafetyAckBand).
 *   GALLERY READ FAILED — said out loud, with the reload control (2026-08-27).
 *                       Never an absence band at all: an outage is not evidence
 *                       the patient uploaded nothing, and the attestation is
 *                       blocked while it stands, naming the failure.
 * The band is the only part that waits for the review, because only it CLAIMS
 * something about the patient; ClinicalSummary owns the review's retry surface.
 *
 * ⚠️ THE MEANING OF "NONE" CHANGED ON 2026-09-04, AND THE BAND HAD TO CHANGE
 * WITH IT (clinic #166 — weight self-certification). Until question set v3 every
 * patient was asked for a photo of themselves on the scales, so no photo meant a
 * patient had not done something they were asked to do, and one loud band was
 * the whole truth. From v3 nobody is asked at sign-up — q14 is a declaration —
 * so for a self-certified patient NO PHOTO IS THE NORMAL CASE, and "Awaiting
 * patient photo. This review cannot conclude without it" would be false twice
 * over: it accuses the patient of ignoring a request never made, and it tells
 * the clinician they are blocked when they are not (the CTO's rule is a WINDOW,
 * not a gate — orders refuses a SECOND dispense only where a request went
 * unanswered).
 *
 * Softening the band for everyone was the wrong fix and was rejected: v1/v2 rows
 * belong to patients who ARE mid-treatment and WERE genuinely asked, and their
 * outstanding photo is exactly the fact this card was built to shout about. So
 * absence now reads through `weightAskState`, and the three readings are:
 *
 *   ASKED, NOT SUPPLIED   → the LOUD band, wording untouched. This is the
 *                           original state and it keeps the original words.
 *   SELF-CERTIFIED        → a plain, quiet statement: no photo on file, the
 *                           patient declared their weight instead, and here are
 *                           the two things staff can do about it. Absence is
 *                           still stated in words on the screen — it is simply
 *                           not dressed as a patient failure. `tone="info"`.
 *   DISCARDED BY RETENTION → a photo DID exist and the 365-day sweep took it
 *                           (`photosDiscarded` on the gallery read, which was on
 *                           the wire and unread until now). Stated as its own
 *                           case because it is the one absence that is nobody's
 *                           fault at all, and the CTO's instruction on it is
 *                           explicit: never a patient failure. It outranks the
 *                           other two — a patient who supplied and then lost
 *                           their photo must never be described as awaiting one.
 *   UNKNOWN ASK           → the review carries no weight question we recognise.
 *                           State the absence, claim nothing about the ask.
 *
 * ⚠️ AND THE TWO STAFF LANES LIVE HERE, on the evidence card rather than in a
 * menu, because they are what "verification is not removed" means in practice:
 * a CLINICIAN or ADMIN can ADD evidence themselves (the shared upload machine,
 * staff lane) or ASK the patient for it (the durable request orders consults
 * before a second dispense). They are offered whatever the photo state is —
 * a clinician may want a fresh image of a patient who already sent one — which
 * is also why neither is a gate.
 *
 * TWO SOURCES, ONE PICTURE. The gallery read is the authority once it answers;
 * the review's own single `photoUrl` is the fallback for the deploy window
 * where clinic-service predates the gallery endpoint (and for a failed gallery
 * read). Falling back on an EMPTY gallery too is deliberate: rendering a photo
 * the review says exists is always safer than hiding one.
 *
 * THE ATTESTATION DOES NOT WAIT FOR THE REVIEW AT ALL — see the note above the
 * final return. It is the only way past a standing approve gate, and the gate
 * is read from a DIFFERENT service, so a failed review read must never take it
 * off the screen.
 *
 * THE URLS EXPIRE. A signed read url is short-lived, so a clinician who leaves
 * the drawer open long enough will hit a dead link. `onError` on each image
 * swaps the tile for a plain "preview expired" note and offers ONE re-fetch
 * control that re-mints the whole set — never a broken-image icon, which reads
 * as "this patient's photo is corrupt" rather than "this link timed out".
 */
function WeightPhotoSection({
  review,
  assessmentId,
  orderCaseId,
  isAdminViewer,
  canRetractEvidence,
  photoReview,
  attestedByName,
  onAttested,
  onReloadReview,
}: {
  review: Loadable<AssessmentReview>;
  /** The assessment the photos hang off — null on an order with no assessment. */
  assessmentId: string | null;
  /** The order the attestation is stamped on — null in review-only mode. */
  orderCaseId: string | null;
  isAdminViewer: boolean;
  /**
   * May this viewer retract a stored photo? SEPARATE from `isAdminViewer`,
   * which also gates the ADMIN-only attestation-name lookup — see the
   * derivation in ReviewView for why one flag could not do both jobs.
   */
  canRetractEvidence: boolean;
  photoReview: Loadable<PhotoReviewStatus>;
  /** The attester's display name — ADMIN viewers only (R19); null shows the role. */
  attestedByName: string | null;
  /** Hand the fresh stamp back so the approve control enables without a reload. */
  onAttested: (status: PhotoReviewStatus) => void;
  /** Re-read the review — the fallback source of the rendered url. */
  onReloadReview: () => void;
}) {
  // The WHOLE read, not just `photos`: `photosDiscarded` is what separates "the
  // retention sweep took it" from "the patient never sent one", and it was on
  // the wire and thrown away until 2026-09-04.
  const [gallery, setGallery] = useState<Loadable<AssessmentPhotosRead>>({ state: 'loading', data: null });
  // Indexes whose <img> fired onError — an expired (or otherwise dead) signed
  // url. Cleared on every re-fetch, because a re-mint gives every tile a fresh
  // url and last render's failures say nothing about this one's.
  const [expired, setExpired] = useState<number[]>([]);
  // A re-read is in flight over photos that are ALREADY on screen (see
  // loadPhotos). Distinct from `gallery.state === 'loading'`, which means there
  // is nothing to show yet.
  const [refreshing, setRefreshing] = useState(false);
  const [enlarged, setEnlarged] = useState<number | null>(null);
  const [attesting, setAttesting] = useState(false);
  const [attestError, setAttestError] = useState<string | null>(null);
  /**
   * THE STAFF UPLOAD LANE (clinic #166) — the SHARED upload machine, not a
   * second one. It brings the five-photo cap, the spend-at-GRANT-time
   * arithmetic (a granted-then-failed PUT has consumed a slot, so a retry
   * re-PUTs the grant it is already holding rather than burning another), the
   * 10 MB pre-check, the 4xx-closes-the-lane rule and the per-tile status
   * words. Re-implementing any of that here is how the three patient lanes
   * drifted before this module existed.
   *
   * `null` when there is no assessment to attach to — the hook must still be
   * CALLED unconditionally (React), so the lane is nulled rather than skipped.
   */
  const staffUploads = useWeightPhotoUploads(assessmentId ? { kind: 'staff', assessmentId } : null);
  /** The evidence REQUEST — the durable ask orders consults before a 2nd dispense. */
  const [confirmingRequest, setConfirmingRequest] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [requestOutcome, setRequestOutcome] = useState<{ notified: boolean } | null>(null);
  /**
   * THE RETRACTION (clinic-service 2026-09-06) — the photo the STAFF viewer is
   * about to destroy (ADMIN **or** CLINICIAN since that day — see
   * `canRetractEvidence`), held by its SERVER ID and never by its tile
   * position: the grid
   * re-reads after every upload and after the retention sweep, so a position
   * captured at click time can point at a different image by the time the
   * dialog is confirmed. `null` means no dialog is open.
   */
  const [retracting, setRetracting] = useState<{ photoId: string; ordinal: number } | null>(null);
  const [retractBusy, setRetractBusy] = useState(false);
  const [retractError, setRetractError] = useState<string | null>(null);
  /** The last completed retraction, so the surface can say what is left. */
  const [retractOutcome, setRetractOutcome] = useState<{ photosRemaining: number } | null>(null);

  /**
   * Re-read the gallery. `'refresh'` KEEPS the photos already on screen while
   * the new read is in flight; `'replace'` (the default, and what a first load
   * or a manual Reload does) blanks them.
   *
   * ⚠️ THE MODE EXISTS BECAUSE THE TILES NO LONGER DOUBLE AS A HOLDING VIEW
   * (2026-09-05). A staff upload that lands fires this re-read, and the upload
   * machine's own `stored` tile is now hidden as a duplicate of the grid — so if
   * this call blanked the grid the way it always has, the photo would vanish
   * from BOTH places for the length of a round trip and the card would read "no
   * photo on file" a beat after somebody added one. Keeping the previous set
   * visible makes the refresh additive rather than a flicker through empty.
   *
   * `refreshing` is tracked separately so the ATTESTATION still waits: the
   * clinician is signing for the whole set, and a set that is mid-re-read is not
   * yet known to be the whole set.
   */
  const loadPhotos = useCallback(
    (mode: 'replace' | 'refresh' = 'replace') => {
      if (!assessmentId) {
        setGallery({ state: 'skip', data: null });
        return;
      }
      setExpired([]);
      if (mode === 'refresh') setRefreshing(true);
      else setGallery({ state: 'loading', data: null });
      getAssessmentPhotos(assessmentId)
        .then((read) => setGallery({ state: 'ready', data: read }))
        .catch(() => setGallery({ state: 'error', data: null }))
        .finally(() => setRefreshing(false));
    },
    [assessmentId],
  );

  useEffect(() => {
    loadPhotos();
  }, [loadPhotos]);

  const r = review.state === 'ready' ? review.data : null;
  const galleryPhotos = gallery.state === 'ready' ? (gallery.data?.photos ?? []) : null;
  /**
   * At least one photo was DISCARDED by the 365-day retention sweep. Absent
   * (an older clinic-service) means "not reported", never "nothing was
   * discarded" — so `=== true`, and an old server simply falls through to the
   * ask-based reading rather than being told a photo once existed.
   */
  const photosDiscarded = gallery.state === 'ready' && gallery.data?.photosDiscarded === true;
  const entries: AssessmentPhotoEntry[] =
    galleryPhotos && galleryPhotos.length > 0
      ? galleryPhotos
      : r && (r.photoUrl || r.photoRef)
        ? [{ photoUrl: r.photoUrl ?? null }]
        : [];
  const hasPhotos = entries.length > 0;
  // Entries that CAME with a url — an expired one still holds its place in the
  // grid (as a plain note), because a photo silently vanishing from a gallery
  // a clinician is attesting to is the worst possible failure mode here.
  const withUrl = entries.filter((entry) => entry.photoUrl !== null).length;
  const previewless = entries.length - withUrl;

  /**
   * Re-mint the signed urls. BOTH sources are re-read on purpose: either one
   * can own the url on screen (the gallery normally, the review during the
   * deploy window or after a failed gallery read), and a reload control that
   * silently refreshes the source you are NOT looking at does nothing.
   */
  const reload = useCallback(() => {
    setExpired([]);
    // 'replace': the operator pressed this BECAUSE what is on screen is wrong
    // (a dead link, a failed read), so holding the old set would be the bug.
    loadPhotos('replace');
    onReloadReview();
  }, [loadPhotos, onReloadReview]);

  /**
   * A STAFF UPLOAD THAT LANDED HAS TO REACH THE GALLERY, or the clinician who
   * just added evidence is looking at a card that says there is none — and, on
   * an order-backed lane, at an attestation control still refusing on "there
   * are no photos on file yet". Keyed on the machine's STORED count so it fires
   * once per confirmed upload and never on a failed PUT (which stores nothing,
   * whatever it did to the slot).
   */
  const storedByStaff = staffUploads.storedCount;
  const previousStored = useRef(0);
  useEffect(() => {
    if (storedByStaff > previousStored.current) {
      previousStored.current = storedByStaff;
      // 'refresh': the grid is the only place this photo will appear now, so it
      // must not blank while we go and fetch it (see loadPhotos).
      loadPhotos('refresh');
    }
  }, [storedByStaff, loadPhotos]);

  /**
   * RETRACT one stored photo. STAFF only — ADMIN **or** CLINICIAN, mirroring
   * clinic-service's `@Roles('ADMIN', 'CLINICIAN')`. See `canRetractEvidence`
   * for the gate and the long note above `photoGrid` for why a clinician DOES
   * get one, and what was argued against it.
   *
   * ⚠️ CORRECTED 2026-09-06. This read "ADMIN only … why a clinician does not
   * get one" for a few hours after the CTO widened the guard, two lines above a
   * gate that already admitted a clinician. Left alone it would have talked the
   * next reader into "aligning" the client back down to ADMIN and silently
   * revoking a decision that was taken deliberately.
   *
   * ⚠️ NOT UNDOABLE, so everything about this path is written for the case where
   * it goes wrong. On success the gallery is RE-READ rather than patched
   * locally: the server's remaining set is the truth, and a spliced-out tile
   * would leave the surface asserting a deletion the server may not have made.
   * On failure the dialog CLOSES and the message lands on the surface behind
   * it (a banner under a fixed overlay is invisible) — and the message matters
   * more here than anywhere else on this screen, because a 503 means the photo
   * IS STILL ON FILE and whoever pressed it must not walk away believing
   * otherwise.
   */
  async function runRetract(reason?: string) {
    if (!assessmentId || !retracting || !reason) return;
    if (retractBusy) return;
    setRetractBusy(true);
    setRetractError(null);
    setRetractOutcome(null);
    try {
      const { photosRemaining } = await retractWeightPhoto(assessmentId, retracting.photoId, reason);
      setRetracting(null);
      setRetractOutcome({ photosRemaining });
      // 'replace', not 'refresh': a tile that is GONE must not linger on screen
      // while the re-read runs. The additive mode exists for an upload landing;
      // here the whole point is that one of the images has stopped existing.
      loadPhotos('replace');
    } catch (err: unknown) {
      // VERBATIM, because every refusal is a different instruction: a 409 says
      // the order has been dispatched and the file is closed in both
      // directions; a 404 says this photo is not on this assessment (or has
      // already gone); a 503 says the bucket could not confirm the delete and
      // NOTHING was removed. PHI-free by construction (R19).
      setRetracting(null);
      setRetractError(
        err instanceof ApiError && err.detail
          ? err.detail
          : /* ⚠️ THE FALLBACK MUST NOT CLAIM THE PHOTO SURVIVED (2026-09-06).
               It used to read "…so it is still on file", and for the one status
               that reaches this branch that sentence is FALSE and the mistake
               is irreversible.

               Every refusal the server states deliberately — 409 dispatched,
               404 not-here, 503 nothing-was-removed — arrives WITH a detail and
               takes the verbatim branch above. What lands here is a 500, and a
               500 means the object delete SUCCEEDED (it runs first, outside the
               transaction, so a storage fault cannot leave the row behind) and
               the row delete then failed. The image is already destroyed, no
               audit row was written, and the old copy sent the operator away
               believing the evidence was safe. Worse, the orphan row is later
               collected by the 365-day sweep, which signs it
               `cause: 'retention_expiry'` — a deliberate staff destruction
               filed as the calendar doing its job, the exact inversion the
               `cause` enum exists to prevent.

               So: say what we actually know, and tell them the one action that
               resolves it. PHI-free (R19) — no name, no ref, no objectRef. */
            'Something went wrong part-way through removing that photo, and we cannot tell you whether it survived. ' +
            'Press Remove again to finish the job, and tell an administrator if it stays on the list.',
      );
    } finally {
      setRetractBusy(false);
    }
  }

  async function askForEvidence() {
    if (!assessmentId) return;
    // A repeat POST is a CHASE at the server — a second durable row AND a
    // second real email — so there is no idempotency to fall back on and the
    // in-flight guard is ours to own. ConfirmDialog's `busy` disables the
    // button too; this is the half that survives someone re-wiring the dialog.
    if (requesting) return;
    setRequesting(true);
    setRequestError(null);
    setRequestOutcome(null);
    try {
      const { notified } = await requestWeightEvidence(assessmentId);
      setConfirmingRequest(false);
      setRequestOutcome({ notified });
    } catch (err: unknown) {
      // VERBATIM. clinic-service 409s a draft / rejected / stopped journey,
      // because a request that can never be answered must not exist — and that
      // sentence is the only thing that tells a clinician why pressing again
      // will not help. PHI-free by construction (R19).
      // Closed on FAILURE too: the outcome regions live outside the dialog
      // (a banner behind a fixed overlay is invisible), and every failure here
      // — a 409 on a stopped journey, a fault — is terminal for this press.
      setConfirmingRequest(false);
      setRequestError(
        err instanceof ApiError && err.detail
          ? err.detail
          : 'Sorry — we could not record that request. Please try again.',
      );
    } finally {
      setRequesting(false);
    }
  }

  /**
   * Does the photos-reviewed gate apply to THIS order?
   *
   * On a ready status the server's `required` is the answer. On a FAILED read
   * we fall back to the same lane fact the server keys on — an assessment
   * behind the order — rather than hiding the control: a clinician who cannot
   * attest cannot approve either, so hiding it during a blip would strand the
   * case with no way out. Attesting on a lane that does not need it is refused
   * by the server, verbatim, which is a far better outcome than a dead end.
   */
  const gateApplies =
    photoReview.state === 'ready'
      ? photoReview.data?.required === true
      : photoReview.state === 'error'
        ? assessmentId !== null
        : false;
  const attestedAt = photoReview.state === 'ready' ? (photoReview.data?.photosReviewedAt ?? null) : null;

  /**
   * WHY the attestation cannot be made yet, or null when it can.
   *
   * THE LOADING CASE IS NOT COSMETIC. While the gallery read is in flight the
   * card falls back to the review's SINGLE photo, so a clinician could attest
   * to "the photos" having been shown one of three. The attestation is a
   * clinical statement about what they looked at, so it waits for the set.
   *
   * THE FAILED CASE IS THE SAME ARGUMENT, HARDER. A failed gallery read used to
   * be SILENT: the error state was written and read nowhere, so the card fell
   * back to the review's one photo (or to nothing) and looked exactly like a
   * complete set. A clinician could then attest — in writing, against a patient
   * record — to having reviewed photos they were never shown. It is checked
   * FIRST because it outranks every other reading of the card: once that read
   * has failed, "there are no photos" and "here are the photos" are both
   * unproven.
   */
  const galleryFailed = gallery.state === 'error';
  const attestBlockedReason = galleryFailed
    ? 'Some photos could not be loaded, so this may not be the full set — reload them before you confirm.'
    : gallery.state === 'loading' || refreshing
      ? 'The photos are still loading — they must all be on screen before you confirm.'
      : !hasPhotos
        ? 'There are no photos on file yet, so there is nothing to confirm you have reviewed.'
        : null;

  /**
   * The load failures worth telling the clinician about, in one place.
   *
   * ONE REGION, ONE CONTROL. A failed gallery read and an expired preview are
   * different sentences with the SAME way out — `reload` re-mints both sources
   * — and two identical "Reload photos" buttons on one card is a choice nobody
   * should have to make.
   */
  const loadFailures: string[] = [];
  if (galleryFailed) {
    // ONE sentence, not one per shape. The review and the gallery resolve
    // independently, so a message worded off `hasPhotos` REWRITES ITSELF under
    // the clinician as the fallback photo arrives — and a line in a live region
    // that changes its own meaning is worse than a slightly blunter one. This
    // is true whether nothing rendered or two of three did.
    loadFailures.push('Some photos could not be loaded, so this card may not show the full set.');
  }
  if (expired.length > 0) {
    loadFailures.push(
      expired.length === 1
        ? 'A photo preview could not be loaded — the link is time-limited and may have expired.'
        : `${expired.length} photo previews could not be loaded — the links are time-limited and may have expired.`,
    );
  }

  async function attest() {
    if (!orderCaseId) return;
    setAttesting(true);
    setAttestError(null);
    try {
      onAttested(await markPhotosReviewed(orderCaseId));
    } catch (err: unknown) {
      // VERBATIM, both of them. "No photo evidence on file" and "the evidence
      // could not be verified — try again" are different situations with
      // different next actions (chase the patient vs retry in a minute), and
      // only the server knows which one happened. Collapsing them into one
      // house message would send a clinician chasing a patient who has already
      // uploaded. The bodies are ids + a state sentence — PHI-free (R19).
      setAttestError(
        err instanceof ApiError && err.detail
          ? err.detail
          : 'Sorry — that could not be recorded. Please try again.',
      );
    } finally {
      setAttesting(false);
    }
  }

  const reviewReady = review.state === 'ready' && review.data !== null;

  /**
   * WAS THIS PATIENT EVER ASKED FOR A PHOTO? (clinic #166 — see
   * `weightDeclaration.ts` for the whole argument and for why it fails toward
   * "asked".) `null` until the review answers: every band below is a CLAIM
   * ABOUT THE PATIENT, and there is no honest claim to make from a read that
   * has not arrived.
   */
  const askState = reviewReady && r ? weightAskState(r) : null;

  /**
   * THE ABSENCE BAND — one of three readings, never silence.
   *
   * It renders only when BOTH reads have answered: a failed gallery read (or a
   * review that never arrived) is not evidence that nobody uploaded anything,
   * and telling a clinician to chase a patient who already uploaded is exactly
   * the mistake the verbatim 409s exist to avoid. `role="status"` (polite),
   * never "alert" — R31's crisis banner owns the one assertive region here.
   *
   * The ORDER of the branches is the judgement (docblock above): retention
   * first, because a photo that existed and was swept is nobody's failure and
   * must never be described as awaited; then the ask.
   */
  const absenceBand =
    !reviewReady || hasPhotos || galleryFailed ? null : photosDiscarded ? (
      <Alert tone="info" role="status" style={{ marginBottom: 0 }}>
        <strong style={{ display: 'block', marginBottom: 'var(--gps-s-1)' }}>No photo on file</strong>
        <p style={{ margin: 0 }}>
          {/* A PHOTO DID EXIST. The 365-day retention sweep discarded it, which
              is the one absence that is nobody's fault — and the CTO's
              instruction on this state is explicit: never render it as a
              patient failure. Saying WHY it is gone is what stops a clinician
              chasing somebody who complied a year ago. */}
          This patient did supply weight evidence, and it has since been discarded &mdash; photos are
          kept for 365 days and then deleted. Ask for fresh evidence below if you need to see it.
        </p>
      </Alert>
    ) : askState === 'photo_asked' ? (
      <Alert tone="warning" role="status" style={{ marginBottom: 0 }}>
        <strong style={{ display: 'block', marginBottom: 'var(--gps-s-1)' }}>Awaiting patient photo</strong>
        <p style={{ margin: 0 }}>
          {/* ⚠️ THIS WORDING IS UNTOUCHED AND STAYS LOUD. It is the original
              band, and it is now shown ONLY to the patients it was written
              about: v1/v2 rows, where the patient WAS asked for an image and
              deferred it. Those people are mid-treatment. Softening this to
              match the self-certified case would take away the one signal the
              card was built to carry.

              WHAT THEY WERE ACTUALLY ASKED FOR — the clinician judges the image
              against it. This said "a photo of the scale reading", which was an
              even older q14 wording; a clinician told the wrong ask accepts the
              wrong evidence. */}
          The patient has not submitted their weight photo yet. This review cannot conclude without
          it &mdash; they were asked for a photo of themselves on the scales, showing them and the
          reading together, and it will appear here the moment they upload it.
        </p>
      </Alert>
    ) : askState === 'self_certified' ? (
      <Alert tone="info" role="status" style={{ marginBottom: 0 }}>
        <strong style={{ display: 'block', marginBottom: 'var(--gps-s-1)' }}>No photo on file</strong>
        <p style={{ margin: 0 }}>
          {/* ⚠️ ABSENCE STATED, NOT IMPLIED — AND NOT AN ACCUSATION. This patient
              was never asked for a photo: they declared their weight instead
              (the card above quotes it). So the sentence says what is missing,
              says why that is expected, and stops. No "awaiting", no "cannot
              conclude" — the second would be plainly false, because nothing
              blocks this review: evidence is a window a clinician may open, not
              a gate every patient must pass. */}
          This patient self-certified their weight and was not asked for a photo, so there is nothing
          on file. Add evidence yourself, or ask them for it, if you want it before you decide.
        </p>
      </Alert>
    ) : (
      <Alert tone="info" role="status" style={{ marginBottom: 0 }}>
        <strong style={{ display: 'block', marginBottom: 'var(--gps-s-1)' }}>No photo on file</strong>
        <p style={{ margin: 0 }}>
          {/* THE ASK IS UNKNOWN — no weight question we recognise is on this
              review. State the absence (that much is certain from the gallery)
              and claim NOTHING about what the patient was asked, in either
              direction. Guessing "awaiting" would accuse them; guessing
              "self-certified" would excuse them. */}
          There is no weight photo on file for this assessment.
        </p>
      </Alert>
    );

  /**
   * The gallery grid and its counts. Everything here needs a URL SET, so it
   * renders whenever there is one — including while the review is still in
   * flight or has failed, where the gallery is the only source left.
   *
   * ═══════════════════════════════════════════════════════════════════════
   * ⚠️ THE "REMOVE" CONTROL DESTROYS ARTICLE 9 CLINICAL EVIDENCE, PERMANENTLY.
   * IT IS STAFF-ONLY (ADMIN **OR** CLINICIAN), IT ASKS FOR A REASON, AND THERE
   * IS NO UNDO.
   * ═══════════════════════════════════════════════════════════════════════
   *
   * THE LANE NOW EXISTS (clinic-service 2026-09-06,
   * `DELETE /v1/assessments/:id/photos/:photoId`). Until it did, this block
   * said so and shipped nothing, because a Remove that only dropped the tile
   * from this render would be worse than the mis-upload it claimed to fix: the
   * operator would believe they had retracted evidence they had not, while the
   * bucket, the gallery read and the prescription gate all still held it.
   *
   * ── ⚠️ IT IS NOT ON THE TILES ANY MORE (CTO, 2026-09-06) ─────────────────
   * It shipped as a "Remove" button under EVERY thumbnail, at rest, on a
   * clinical record — the rarest action on this card wearing the most
   * prominent chrome on it. It now lives one level down, in the ⋯ menu of the
   * PHOTO VIEWER, so whoever is about to destroy an image has to OPEN it first.
   * That is the safety property the move buys, not just tidiness:
   * the old grid put "Remove" for photo 2 within 8px of "Enlarge" for photo 3,
   * and the whole lane exists because somebody already made one mis-click.
   *
   * The tiles are therefore READ-ONLY for every role, with ONE named exception:
   * the "Preview expired" tile keeps its own Remove, because PhotoLightbox
   * returns null on a dead url and there is no viewer for it to borrow (see the
   * comment on that branch). `isAdminViewer` no longer appears in the grid at
   * all — the gate travels with the control, and lives on the `onRetract` prop
   * handed to `PhotoLightbox` below and on `canRetractEvidence` in that one
   * fallback.
   *
   * ⚠️ AND THE VIEWER CLOSES THE MOMENT THE ROW IS CHOSEN. The confirm, the
   * outcome and every failure render from the CARD BODY (see `retractFeedback`
   * — it is a separate const for exactly this reason). Two stacked fixed
   * overlays would hide the surface those messages describe, and there is a
   * concrete second bug behind the same rule: the viewer's arrow-key handler
   * is on the document, so leaving it mounted under the confirm would let
   * ArrowLeft/ArrowRight step the photo — and `preventDefault` the caret —
   * while the operator is typing the reason the audit trail depends on.
   *
   * ── WHO GETS THE CONTROL, and the reasoning, so it is not re-litigated ────
   * ⚠️ CORRECTED 2026-09-06. This block read "ADMIN only, matching the server's
   * `@Roles('ADMIN')` exactly", and it said so two lines above a gate that
   * already admitted a clinician — the guard had been widened that morning and
   * the comment had not moved. A reader who trusted it would have "aligned" the
   * client back down to ADMIN and quietly revoked a decision the CTO made on
   * purpose. The rule the sentence stated is still right: a control the server
   * would 403 is not a control, and a role that can act and is not offered one
   * is a different bug, so THIS GATE AND THAT GUARD HAVE TO MOVE TOGETHER —
   * which is exactly the obligation that went unmet for those few hours.
   *
   * STAFF, both roles: `@Roles('ADMIN', 'CLINICIAN')` on
   * `DELETE /v1/clinic/assessments/:id/photos/:photoId`, mirrored here by
   * `canRetractEvidence`.
   *
   *  - PATIENT: never, and this half did not move. Deleting an image a
   *    clinician has already weighed a prescribing decision against destroys
   *    the evidence behind a POM supply that has happened, and the record has
   *    to outlive their second thoughts. Erasure requests are a DPO lane with
   *    an Article 9 balancing test (UK GDPR Art. 17(3)(b)/(h)), not a button,
   *    and the 365-day retention sweep already bounds how long we keep it.
   *  - CLINICIAN: YES since 2026-09-06 — BY AN EXPLICIT CTO DECISION, OVER A
   *    RAISED OBJECTION. Both halves are kept here because the next reader
   *    deserves to know this was judged and not overlooked.
   *
   *    ⚠️ THE ARGUMENT AGAINST, put and overruled: a clinician is the JUDGE of
   *    this evidence, so letting the person who signs "I reviewed these photos"
   *    also decide which photos exist puts the attestation and the record it is
   *    made against in one pair of hands. The hand-off — the clinician adds the
   *    correct image (the picker below) and asks an admin to retract the wrong
   *    one — WAS the two-person control, and admitting CLINICIAN dissolves it.
   *
   *    THE DECISION: the case that actually happens is a clinician opening a
   *    file and finding ANOTHER PATIENT'S photo on it. That is a live
   *    personal-data breach, spotted by the only person looking at the record,
   *    and queuing it behind an admin only prolongs it. What replaces the
   *    second pair of hands is the TRAIL — every retraction writes an
   *    append-only `weight_evidence_removed` row carrying the verified actor
   *    and their role (taken from the session, never from the body), a
   *    MANDATORY reason, and `cause: 'staff_correction'` so it stays
   *    distinguishable from the retention sweep forever. Detection after the
   *    fact instead of prevention before it: a weaker control, chosen
   *    deliberately, and it holds only while the reason stays mandatory.
   *  - ADMIN: yes, as before. The clerical case — the wrong file for the
   *    patient in front of them — and the admin desk already owns every other
   *    record-hygiene reversal in this estate.
   *
   * ── WHAT CHANGED FROM THE SHAPE THIS BLOCK USED TO SPECIFY ───────────────
   * It also called for a refusal once `photosReviewedAt` was stamped. The
   * server does NOT implement one, on two grounds worth recording here because
   * this is where a reader will look for it: the attestation lives on the ORDER
   * in orders-service and clinic-service cannot see it, AND a hard block would
   * keep another patient's photo — a personal-data breach — in a clinical file
   * permanently. Clinical records handle this by appending the correction,
   * which is exactly what the audit row is. The bound the server DOES enforce
   * is `orderDispatchedAt`, and its 409 surfaces here verbatim.
   *
   * ── A PHOTO WITH NO `photoId` GETS NO MENU ───────────────────────────────
   * A legacy `Assessment.photoRef` with no ledger row has no id, and the wire
   * field is `null` for it. There is no retract lane for those and inventing an
   * address would aim the delete at nothing (or, worse, at something else), so
   * the ⋯ is simply absent on that photo — not a disabled row, which would
   * promise a control we cannot honour. The check is per-PHOTO (`photo.photoId`
   * inside the viewer) and not a truthiness test on the gallery, because an
   * older clinic-service reporting no ids at all and one legacy pointer among
   * five real rows have to behave the same way.
   */
  const photoGrid = hasPhotos ? (
    <>
      {withUrl > 0 ? (
        <>
          {/* A NAMED list so the grid is one announced group rather than a
              run of loose buttons, and each control carries its position in
              its accessible name (WCAG 1.3.1 / 2.4.6). */}
          <ul
            aria-label="Submitted weight photos"
            style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(104px, 1fr))', gap: '8px' }}
          >
            {entries.map((entry, index) =>
              entry.photoUrl === null ? null : (
                // The RENDER key is still the position — the order is the
                // server's stable one (legacy pointer, then rows oldest-first)
                // and React only needs it to be stable within one render. The
                // RETRACT is keyed on `entry.photoId` instead, because that
                // has to stay correct across a re-read: positions shift under
                // every upload and every sweep, and a delete aimed by position
                // would eventually take the wrong image.
                <li key={`photo-${index}`}>
                  {expired.includes(index) ? (
                    /* ⚠️ AN EXPIRED TILE STILL NEEDS A WAY OUT (found in review,
                       2026-09-06). This branch used to be a bare <div>, which was
                       harmless while a per-tile Remove sat beside it — and became
                       a trap the moment removal moved into the viewer, because
                       nothing here opens the viewer and PhotoLightbox returns null
                       on a dead url. So the ONE photo a staff member most needs
                       to retract was the one they could not reach.
                       The unrecoverable case is this lane's own worst outcome: a
                       500 destroys the object and leaves the row, the tile reads
                       "Preview expired" forever, and 365 days later the sweep
                       files that deliberate destruction as `retention_expiry` —
                       exactly the inversion the `cause` enum exists to prevent.
                       So the tile keeps its own Remove. Same `canRetractEvidence`
                       gate (ADMIN or CLINICIAN, mirroring the server), same
                       photoId aim, same confirm — it simply cannot borrow the
                       viewer's, because there is no viewer to open. */
                    <div
                      style={{ minHeight: '104px', display: 'grid', placeItems: 'center', gap: '6px', padding: '8px', textAlign: 'center', border: '1px dashed var(--gps-border)', borderRadius: 'var(--gps-r-md)', color: 'var(--gps-muted)', fontSize: '12px' }}
                    >
                      <span>Preview expired</span>
                      {canRetractEvidence && entry.photoId ? (
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => {
                            setRetractError(null);
                            setRetractOutcome(null);
                            setRetracting({ photoId: entry.photoId as string, ordinal: index + 1 });
                          }}
                          aria-label={`Remove ${photoAlt(index, entries.length)}`}
                          style={{ width: 'auto', minHeight: '32px', padding: '4px 10px', fontSize: '12px' }}
                        >
                          Remove
                        </Button>
                      ) : null}
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setEnlarged(index)}
                      aria-label={`Enlarge ${photoAlt(index, entries.length)}`}
                      style={{ display: 'block', width: '100%', minWidth: '44px', minHeight: '44px', padding: 0, overflow: 'hidden', background: 'none', border: '1px solid var(--gps-border)', borderRadius: 'var(--gps-r-md)', cursor: 'pointer' }}
                    >
                      <img
                        src={entry.photoUrl}
                        alt={photoAlt(index, entries.length)}
                        onError={() => setExpired((prev) => (prev.includes(index) ? prev : [...prev, index]))}
                        style={{ display: 'block', width: '100%', height: '104px', objectFit: 'cover' }}
                      />
                    </button>
                  )}
                  {/* NOTHING ELSE RIDES ON THE TILE. The retraction used to
                      sit here as a per-tile "Remove" button — see the block
                      above photoGrid for why it moved into the viewer. */}
                </li>
              ),
            )}
          </ul>
          <p style={{ color: 'var(--gps-muted)', fontSize: '12px', margin: 'var(--gps-s-2) 0 0' }}>
            {entries.length === 1
              ? 'This link is time-limited and will expire — it is not a permanent copy.'
              : `${entries.length} photos on file. These links are time-limited and will expire — they are not permanent copies.`}
          </p>
        </>
      ) : null}

      {previewless > 0 ? (
        <Help style={{ marginBottom: 0, marginTop: withUrl > 0 ? 'var(--gps-s-2)' : 0 }}>
          {entries.length === 1
            ? 'Photo on file — preview unavailable on this environment'
            : `${previewless} of ${entries.length} photos on file — preview unavailable on this environment`}
        </Help>
      ) : null}
    </>
  ) : null;

  /**
   * WHAT DID NOT LOAD, AND THE WAY OUT — the single recovery region.
   *
   * A dead link is a TIMEOUT, not a corrupt photo, and a failed gallery read is
   * an outage, not an empty gallery. Both are said in words and both are
   * offered the SAME control, which re-mints from both sources. Neither may be
   * silent: the failed read in particular used to set a state nothing rendered,
   * which is how an outage came to look identical to "no photos on file".
   * role="status" (polite) keeps R31 the only assertive region on this surface.
   */
  const recoveryRegion =
    loadFailures.length > 0 ? (
      <div role="status" style={{ marginTop: 'var(--gps-s-2)' }}>
        {loadFailures.map((line) => (
          <p key={line} style={{ color: 'var(--gps-text)', fontSize: '13px', margin: '0 0 8px' }}>
            {line}
          </p>
        ))}
        <Button type="button" variant="ghost" onClick={reload} style={{ width: 'auto', minHeight: '44px', padding: '10px 18px' }}>
          Reload photos
        </Button>
      </div>
    ) : null;

  /* ── THE ATTESTATION (CTO 2026-08-27) ──────────────────────────────────
     "Only after the clinician sees the photo can they finalise." The
     BUTTON is CLINICIAN-only — attesting is a clinical act and the
     server's @Roles says the same — so an ADMIN gets the waiting line
     instead of a control that could only ever 403. The STAMP is shown to
     BOTH: an admin needs to know why the case is (or is no longer)
     waiting. Nothing here renders on a lane the gate does not apply to. */
  const attestation =
    orderCaseId && gateApplies ? (
      <div style={{ marginTop: 'var(--gps-s-3)', paddingTop: 'var(--gps-s-3)', borderTop: '1px solid var(--gps-border)' }}>
        {attestedAt ? (
          <p style={{ color: 'var(--gps-muted)', fontSize: '13px', margin: 0 }}>
            Photos reviewed by {attestedByName ?? 'a clinician'} at {formatUtcDateTime(attestedAt)}
          </p>
        ) : isAdminViewer ? (
          <Help style={{ marginBottom: 0 }}>Waiting for the clinician to confirm they have reviewed the photos.</Help>
        ) : (
          <>
            <Button
              type="button"
              variant="cta"
              disabled={attestBlockedReason !== null || attesting}
              aria-describedby={attestBlockedReason ? ATTEST_BLOCKED_REASON_ID : undefined}
              onClick={() => void attest()}
              style={{ width: 'auto', minHeight: '44px', padding: '10px 18px' }}
            >
              {attesting ? 'Recording…' : 'Mark photos as reviewed'}
            </Button>
            {/* WHY the control will not work, in words tied to it — never a
                bare `disabled` and never a hover-only hint, which a keyboard
                or touch user never sees (WCAG 3.3.1). */}
            {attestBlockedReason ? (
              <p id={ATTEST_BLOCKED_REASON_ID} style={{ fontSize: '13px', color: 'var(--gps-muted)', margin: '8px 0 0', lineHeight: 1.45 }}>
                {attestBlockedReason}
              </p>
            ) : null}
            {attestError ? (
              <Alert tone="danger" role="status" style={{ marginTop: 'var(--gps-s-2)', marginBottom: 0 }}>
                {attestError}
              </Alert>
            ) : null}
          </>
        )}
      </div>
    ) : null;

  /**
   * THE ATTESTATION IS NOT REVIEW-SHAPED, so it does not sit behind the review.
   *
   * It needs three things and the review is none of them: the ORDER (which the
   * caller already holds), the photos-reviewed status (orders-service) and the
   * gallery (its own clinic-service read). The card used to return null on any
   * non-ready review, which produced a real dead end: clinic-service's review
   * read fails while orders-service answers happily, so `photosGateBlocks`
   * disables Approve — and the one control that clears it is not on the page at
   * all. A clinician with a blocked Approve, no attest button and no
   * explanation has nothing left to do but reject a case that was probably
   * fine. Nothing below reads `review`, so nothing below has to wait for it.
   *
   * The card still renders NOTHING when it has nothing to say — an order-less
   * review whose gallery is empty is silent exactly as it was.
   */
  /**
   * THE TWO STAFF LANES (clinic #166) — "anyone must be able to add a photo"
   * (CTO), and a clinician may ask the patient for one at any time.
   *
   * ⚠️ CLINICIAN AND ADMIN BOTH, unlike the attestation beside it, and the
   * split is the server's: `photo-upload/staff` and `evidence-request` are
   * `@Roles('CLINICIAN','ADMIN')` because obtaining evidence is not a clinical
   * judgement, while `photos-reviewed` is CLINICIAN-only because ATTESTING is.
   * The portal mirrors that rather than inventing its own line.
   *
   * ⚠️ NEITHER IS A GATE, AND NEITHER IS HIDDEN ONCE A PHOTO EXISTS. A clinician
   * may want a fresher image of a patient who already sent one — a declaration
   * made three months ago is not evidence about today — and the CTO's whole
   * frame here is a WINDOW rather than a gate. An affordance that disappeared
   * as soon as one photo landed would quietly rebuild the mandatory step this
   * change exists to remove.
   *
   * Absent entirely without an assessment: there is nothing to attach evidence
   * to, and nobody to ask.
   */
  const staffEvidence = assessmentId ? (
    <div style={{ marginTop: 'var(--gps-s-3)', paddingTop: 'var(--gps-s-3)', borderTop: '1px solid var(--gps-border)' }}>
      <p style={{ margin: '0 0 var(--gps-s-2)', fontSize: '13px', color: 'var(--gps-muted)', lineHeight: 1.45 }}>
        Weight evidence can be added by the patient, an admin or a clinician at any time.
      </p>
      {/* ONE ROW, AND A CLEAR FIRST CHOICE (CTO 2026-09-05). These read as a
          pair and were rendered as one, but the picker took the kit's default
          `width: 100%`, so in a wrapping flex row its hypothetical main size
          filled the line and pushed the ask onto a second one — two stacked
          full-bleed ghosts that looked like two unrelated decisions. Both now
          size to their label, so they share a line and still wrap cleanly on a
          narrow drawer instead of overflowing.

          The variants carry the ranking, not the order alone: staff usually
          have the evidence in hand (the patient sent it by WhatsApp, or read
          their scales out on the phone), so ADDING it is the primary move and
          takes the green `primary`. Asking is the fallback for when they do
          not, and stays a bordered `ghost`. Neither is `cta` — terracotta is
          reserved for the one conversion action per screen, and on the Review
          tab that is the clinical decision, not an upload. */}
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
        <WeightPhotoPicker
          id="staff-weight-photo"
          label="Add evidence"
          busyLabel="Adding…"
          busy={staffUploads.busy}
          disabled={staffUploads.laneClosed || staffUploads.remaining === 0}
          variant="primary"
          icon={<IconUploadPhotos />}
          style={{ width: 'auto', minHeight: '44px', padding: '10px 18px' }}
          onFiles={staffUploads.addFiles}
        />
        <Button
          type="button"
          variant="ghost"
          disabled={requesting}
          onClick={() => setConfirmingRequest(true)}
          style={{ width: 'auto', minHeight: '44px', padding: '10px 18px' }}
        >
          {/* Dropped while asking — see the picker beside it. */}
          {requesting ? null : <IconRequestPhotos />}
          {requesting ? 'Asking…' : 'Ask patient for evidence'}
        </Button>
      </div>
      {/* ⚠️ CONFIRM BEFORE IT LEAVES (CTO 2026-09-05). This used to POST on the
          click. It is not an undoable UI action: the server appends a durable
          `WeightEvidenceRequest` row that orders-service consults before a
          second dispense, writes an audit row, and emails the patient — and a
          repeat press is a CHASE, not a no-op, so a mis-click cannot be taken
          back by pressing nothing. Same grammar as the plan-link re-send: name
          what lands, name what does not change, and never overstate the second
          half — unlike that one, this ask DOES record something, so the copy
          says so rather than promising a clean record. */}
      <ConfirmDialog
        open={confirmingRequest}
        title="Ask the patient for weight evidence?"
        confirmLabel="Send the request"
        busy={requesting}
        onCancel={() => setConfirmingRequest(false)}
        onConfirm={() => void askForEvidence()}
        body={
          <>
            <p style={{ margin: '0 0 8px' }}>
              Emails the patient at the address on their record, asking them to send weight evidence &mdash;
              a photo of their scales, or another acceptable proof.
            </p>
            <p style={{ margin: 0 }}>
              No clinical decision is made and nothing they have already sent is altered. The ask itself
              is recorded on the journey, a further dispense is held while it goes unanswered, and asking
              again is logged as a chase and sends another email.
            </p>
          </>
        }
      />
      {/* The machine's own tiles and cap line, in the STAFF voice — one copy
          module, two voices (see WeightPhotoUploads). The tiles are already a
          polite live region, which is what announces a PUT finishing. */}
      {/* ⚠️ NO TILE FOR A PHOTO THAT IS ALREADY IN THE GRID ABOVE (2026-09-05,
          CTO: after an admin adds evidence "the drawer shows the photo twice").

          The grid at the top of this card is the ONE view of what is on file —
          it is the server's read, it is what the attestation is made against,
          and a stored upload joins it as soon as the re-read returns. The tile
          strip repeated that same image with a second caption and a second
          count, so the card answered "how many photos does this patient have?"
          twice and disagreed with itself whenever the re-read lagged.

          WHAT THE STRIP STILL CARRIES, because none of it is in the grid: the
          in-flight progress bar, "Didn't send", the too-large explanation and
          the per-file Try again / Remove. Those tiles describe files the bucket
          does NOT have, so hiding the whole list — rather than just the stored
          duplicates — would have silently removed the only place a failed
          upload is visible and retryable. The SESSION COUNT it also carried
          lives on in the cap line directly below. */}
      <WeightPhotoTiles uploads={staffUploads} voice="staff" storedShownElsewhere />
      <div style={{ marginTop: 'var(--gps-s-2)' }}>
        <WeightPhotoCapLine uploads={staffUploads} voice="staff" />
      </div>
      {/* ⚠️ THE REQUEST'S OUTCOME IS TWO DIFFERENT FACTS AND THEY MUST NOT
          COLLAPSE. The row is recorded either way — the clinical decision to
          ask HAPPENED, and orders-service will refuse a second dispense on it —
          but on a stub mail env, a deduped replay, a leadless journey or a mail
          fault the patient never heard about it. `notified: false` therefore
          says "phone them", because a clinician who reads "asked" and waits is
          waiting on evidence nobody requested out loud. Live region: the answer
          arrives after a press with nothing else on screen changing. */}
      <div role="status" aria-live="polite">
        {requestOutcome ? (
          <p style={{ margin: 'var(--gps-s-2) 0 0', fontSize: '13px', color: 'var(--gps-text)', lineHeight: 1.45 }}>
            {requestOutcome.notified
              ? 'Recorded — the patient has been emailed asking for weight evidence.'
              : 'Recorded, but the email did not go out. Phone the patient, or ask again later — the request itself is on file either way.'}
          </p>
        ) : null}
      </div>
      {requestError ? (
        <Alert tone="danger" role="status" style={{ marginTop: 'var(--gps-s-2)', marginBottom: 0 }}>
          {requestError}
        </Alert>
      ) : null}
    </div>
  ) : null;

  /**
   * ⚠️ THE RETRACTION'S OUTCOME LIVES OUTSIDE `photoGrid`, AND THAT IS THE
   * WHOLE REASON IT IS A SEPARATE CONST.
   *
   * It sat inside the grid first, which unmounted it in exactly the case that
   * matters most: retract the LAST photo and `hasPhotos` goes false, the grid
   * returns null, and "Photo removed — there is now no weight evidence on file"
   * vanishes with it. The screen would answer a destructive press with silence
   * and an absence band that reads as though the patient never sent anything.
   *
   * The FAILURE half is worse still if it is ever lost: a 503 means the bucket
   * could not confirm the delete and NOTHING was removed, and a staff member
   * who walks away believing they retracted a wrong-patient image when they did
   * not is the worst outcome this lane has. Both are rendered from the card body,
   * unconditionally, and never inside an overlay — a fixed panel hides the
   * surface the message is about. There are TWO overlays to keep out of now
   * (2026-09-06): the confirm closes on both paths, and the PHOTO VIEWER the
   * ⋯ menu lives in is closed before the confirm ever opens.
   */
  const retractFeedback = (
    <>
      {/* Live region: nothing else on screen changes when a retraction lands. */}
      <div role="status" aria-live="polite">
        {retractOutcome ? (
          <p style={{ margin: 'var(--gps-s-2) 0 0', fontSize: '13px', color: 'var(--gps-text)', lineHeight: 1.45 }}>
            {retractOutcome.photosRemaining === 0
              ? 'Photo removed. There is now no weight evidence on file for this patient.'
              : `Photo removed. ${retractOutcome.photosRemaining} ${retractOutcome.photosRemaining === 1 ? 'photo remains' : 'photos remain'} on file.`}
          </p>
        ) : null}
      </div>
      {retractError ? (
        <Alert tone="danger" role="status" style={{ marginTop: 'var(--gps-s-2)', marginBottom: 0 }}>
          {retractError}
        </Alert>
      ) : null}
    </>
  );

  /**
   * THE RETRACTION CONFIRM — `tone="danger"` and `requireReason`, which are the
   * two things that make this different from every other dialog on the screen.
   *
   * The copy names WHAT IS LOST and says it cannot be undone, because both are
   * true and neither is recoverable by pressing nothing: the image leaves the
   * bucket, the row leaves the database, and the only thing left afterwards is
   * an audit entry saying it once existed. It also says what SURVIVES — the
   * clinician's attestation, the other photos, the patient's declaration — so
   * whoever is correcting one mis-upload is not left wondering whether they
   * have just unpicked the whole review. (It says "any clinician attestation
   * already made" for a reason: since 2026-09-06 the reader may BE that
   * clinician, and the copy has to hold either way.)
   *
   * The reason is required by the SERVER (400 without one) and required here so
   * the refusal never has to happen: it is the only place the WHY of a
   * destroyed piece of clinical evidence can ever be read from, and "wrong
   * patient" — a personal-data breach — and "unreadable" lead to completely
   * different follow-ups.
   *
   * Rendered OUTSIDE the card fragment above and mounted unconditionally on
   * `retracting`, so it survives a re-read of the gallery underneath it.
   */
  const retractDialog = retracting ? (
    <ConfirmDialog
      open
      tone="danger"
      title={`Remove photo ${retracting.ordinal} from this patient's file?`}
      confirmLabel={retractBusy ? 'Removing…' : 'Remove this photo permanently'}
      busy={retractBusy}
      requireReason
      reasonLabel="Why is this photo being removed?"
      onCancel={() => setRetracting(null)}
      onConfirm={(reason) => void runRetract(reason)}
      body={
        <>
          <p style={{ margin: '0 0 8px' }}>
            <strong>This deletes the image itself, and it cannot be undone.</strong> The photo is erased
            from storage and from this patient&rsquo;s file — there is no copy and no recycle bin.
          </p>
          <p style={{ margin: '0 0 8px' }}>
            What stays: the other photos on file, the patient&rsquo;s weight declaration, and any
            clinician attestation already made. A record that this photo existed and was removed —
            with your name, the time and the reason below — is kept on the audit trail permanently.
          </p>
          <p style={{ margin: 0, fontSize: '13.5px', color: 'var(--gps-muted)' }}>
            The upload slot comes back, so the patient or a member of staff can add a replacement.
          </p>
        </>
      }
    />
  ) : null;

  // `retractDialog` is deliberately NOT in this guard: it can only be open when
  // a tile was on screen to press, so the card is rendering by construction.
  if (!absenceBand && !photoGrid && !recoveryRegion && !attestation && !staffEvidence) return null;

  return (
    <Card>
      {/* "Weight evidence", not "Weight photo": this card now holds the
          gallery, the staff upload and the request, and a photo is one form the
          evidence can take rather than the whole of it (clinic #166 — "which
          may include a photograph of your weighing scales, a photograph/video
          verification or another appropriate form of evidence"). */}
      <SectionTitle>Weight evidence</SectionTitle>
      {absenceBand}
      {photoGrid}
      {retractFeedback}
      {recoveryRegion}
      {attestation}
      {staffEvidence}
      {retractDialog}

      {/* Enlarge — the SHARED viewer, which the admin gallery mounts too, so
          the two consoles show the same evidence the same way. It also brings
          Previous/Next: patients upload several angles and the decision is made
          by looking ACROSS them, which closing and reopening per photo made a
          chore (CTO, 2026-09-02) — and, since 2026-09-06, the ⋯ menu that is
          now the only way to reach the retraction. */}
      {enlarged !== null ? (
        <PhotoLightbox
          /* url + WHO + WHICH ROW, one object per photo. A parallel array would
             let the role slip a photo, and it would let the DELETE ADDRESS slip
             one — which is the same mis-aim this lane exists to correct (see
             PhotoLightboxItem). */
          photos={entries.map((e, i) => ({
            url: expired.includes(i) ? null : e.photoUrl,
            addedByRole: e.addedByRole,
            photoId: e.photoId,
          }))}
          index={enlarged}
          onIndex={setEnlarged}
          onClose={() => setEnlarged(null)}
          onExpired={(i) => setExpired((prev) => (prev.includes(i) ? prev : [...prev, i]))}
          /* ⚠️ THE ROLE GATE, AND THE ONLY ONE IN THE UI. Passing `undefined`
             renders no ⋯ at all, which is what a non-staff viewer gets.

             ⚠️ CORRECTED 2026-09-06 — this said "passing `undefined` for a
             CLINICIAN … the judge of this evidence does not also curate it",
             and cited the server as `@Roles('ADMIN')`, sitting two lines above
             a ternary that already admitted one. The guard is
             `@Roles('ADMIN', 'CLINICIAN')` and has been since that morning; the
             CTO widened it over a raised objection, and the objection is kept
             in full in the block above photoGrid rather than deleted. Anyone
             narrowing this back to ADMIN is reverting a decision, not fixing a
             mismatch.

             The server's guard is still the actual control (R41); this only
             decides whether we offer a control that would work. The viewer
             separately withholds the row from a photo with no `photoId`, so a
             legacy pointer is unreachable even here.

             ⚠️ CLOSING THE VIEWER IS PART OF THE CONTRACT, not tidying up: the
             confirm and everything it produces belong on the card behind this
             overlay (see `retractFeedback`). Any stale message from a previous
             attempt is cleared here too — a red 503 banner still on screen
             while a NEW confirm is open is a reader taking last time's outcome
             for this time's. */
          onRetract={
            canRetractEvidence
              ? (photoId, i) => {
                  setEnlarged(null);
                  setRetractError(null);
                  setRetractOutcome(null);
                  setRetracting({ photoId, ordinal: i + 1 });
                }
              : undefined
          }
        />
      ) : null}
    </Card>
  );
}

/* THE PRESCRIBED-LINE RESOLVER AND ITS RENDERER LIVED HERE UNTIL 2026-09-13.
 *
 * This file carried a private `NotRecorded` / `recordedOr` / `PrescribedItemDetail`
 * / `PrescribedItems` set — a SECOND answer to "what does this prescribed line
 * say", sitting beside `lib/prescribedLine.ts`. The two disagreed on the one
 * field that matters: this copy fell back to the stored `medicationName`
 * UNCONDITIONALLY, so the auto-issue lane's machine default (`medicationName`
 * persisted EQUAL to the raw `productKey`) rendered in the Medication row as
 * though a prescriber had chosen it — the founding defect of this whole
 * programme, reproduced inside the screen built to remove it.
 *
 * Both are now `lib/prescribedLine.ts` (the resolver) and `ui/PrescribedItems.tsx`
 * (the one renderer over it), on the strict rule: a stored name equal to the
 * product key is NOT a name a human chose, so it resolves to absent and the
 * surface flags it. `lib/prescribedDirections.ts` and `lib/prescribedQuantity.ts`
 * folded into the same module for the same reason — one line, one answer, one
 * place. Do not reintroduce a local copy of any of them.
 */
/**
 * "Previously issued prescriptions" — every prescription issued for this case,
 * newest first (GET /v1/orders/:orderCaseId/prescriptions). Money-free by
 * construction: never render £ here (there is nothing to render). The repeat
 * chip wording ("Repeat monthly") is aligned with the checkbox on PrescribeForm.
 */
function PrescriptionHistory({
  prescriptions,
  onRetry,
}: {
  prescriptions: Loadable<StaffPrescriptionsResponse>;
  onRetry: () => void;
}) {
  return (
    <Card>
      <SectionTitle>Previously issued prescriptions</SectionTitle>
      {prescriptions.state === 'loading' ? (
        <Spinner label="Loading prescription history…" />
      ) : prescriptions.state === 'error' || !prescriptions.data ? (
        <ErrorState message="Sorry — we could not load the prescription history." onRetry={onRetry} />
      ) : prescriptions.data.items.length === 0 ? (
        <Help style={{ marginBottom: 0 }}>No prescriptions issued yet.</Help>
      ) : (
        /* NAMED, not just titled: the list is the thing a reader navigates, and
           `list-style: none` is enough for some browsers to drop the list
           semantics entirely. The name also lets a test address THIS panel
           rather than sweeping the whole review for a word like "Not
           recorded", which several unrelated fields on this screen also use. */
        <ul aria-label="Previously issued prescriptions" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {prescriptions.data.items.map((rx) => (
            <li key={rx.prescriptionId} style={{ padding: '10px 0', borderBottom: '1px solid var(--gps-border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--gps-s-2)', flexWrap: 'wrap' }}>
                <span style={{ color: 'var(--gps-text)', fontWeight: 600, fontSize: '14px' }}>{formatUtcDate(rx.issuedAt)}</span>
                {rx.repeatFlag ? <Badge tone="pending">Repeat monthly</Badge> : null}
                {rx.pdfRef ? (
                  <Badge tone="positive">
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      PDF stored <CheckIcon size={12} />
                    </span>
                  </Badge>
                ) : null}
              </div>
              {/* ⚠️ NO CORRECTION CONTROL LIVES HERE ANY MORE (2026-09-12). The
                  `MedicationCorrection` panel that used to hang off the newest
                  prescription POSTed a route orders-service had deleted, so it
                  was a 404 button mounted for every clinician on every case with
                  a prescription. Re-prescribing a v1 backlog order is offered on
                  the DECISION card instead, where prescribing already lives, and
                  it goes through the ordinary prescribe endpoint. */}
              {/* FULL dispensing detail, not a one-line summary, through the ONE
                  resolver + ONE renderer the admin drill-down, the deal drawer
                  and the dispensing console also use. This is the clinician's
                  clinical record of what was prescribed and it must agree
                  field-for-field with the signed PDF and the pharmacy line, so
                  it cannot have a formatter of its own. An unrecorded field is
                  FLAGGED, never concatenated into whitespace. */}
              <div style={{ marginTop: '8px' }}>
                <PrescribedItems items={rx.items} emptyText="No items on file." />
                {/* SUPPLIES — their own block under the medication, never a
                    medication line (see ui/PrescribedSupplies). */}
                <PrescribedSupplies supplies={rx.supplies} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/**
 * The consent answer as a TICK-LIST (2026-08-26 §2). The server pre-joins the
 * ticked declarations into one "; "-separated string, which rendered as a wall
 * of text nobody could scan against the checkbox list the patient actually
 * saw. Split back on the SERVER'S OWN join token — each declaration gets its
 * line and a check glyph. A single-part answer (one declaration, no join)
 * still renders as one ticked line, so consent reads as things-that-were-agreed. The
 * glyph is decorative (CheckIcon is aria-hidden); the visible text carries the
 * meaning.
 */
function ConsentAnswerList({ answer }: { answer: string }) {
  const parts = answer
    .split('; ')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
  if (parts.length === 0) return <>{answer}</>;
  return (
    // Named list so assistive tech (and the specs) can tell the declarations
    // apart from the surrounding Q/A definition list.
    <ul aria-label="Consent declarations" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '4px' }}>
      {parts.map((part) => (
        <li key={part} style={{ display: 'flex', alignItems: 'baseline', gap: '6px', color: 'var(--gps-text)' }}>
          <span style={{ color: 'var(--gps-green-text)' }}>
            <CheckIcon size={12} />
          </span>
          <span>{part}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Is this item the WEIGHT-PHOTO question — the v1/v2 ask, not the v3
 * declaration that replaced it (clinic #166, 2026-09-04)?
 *
 * ⚠️ IT IS NO LONGER "IS THIS q14". It used to be `questionKey === 'q14' ||
 * prompt mentions photo`, and the first half of that is now WRONG for every new
 * patient: q14 still occupies the slot but it asks for no photo. Left alone, the
 * Questionnaire tab's `photoState` mode would have replaced a self-certified
 * patient's declaration with `PhotoStateAnswer`'s "Patient chose: add before
 * dispatch" — a sentence about a choice they were never offered, standing where
 * the undertaking they actually gave should be.
 *
 * So the PROMPT is the detector now, and it is the honest one: the review
 * projects the prompt from the patient's OWN question set, so it is evidence of
 * what THEY were shown rather than of which key the estate happens to use. A
 * prompt mentioning a photo is the ask; anything else in that slot is not.
 */
function isWeightPhotoItem(item: { questionKey: string; prompt: string }): boolean {
  return /photo/i.test(item.prompt);
}

/**
 * The q14 answer as the ACTUAL photo state (2026-08-27 drawer restructure —
 * "hard to see what they submitted"): the stored answer is the patient's CHOICE
 * at submit time ("I'll add this before…"), which goes stale the moment they
 * upload. So the slot reports what is true NOW.
 *
 * ⚠️ IT NO LONGER RENDERS A THUMBNAIL, AND NO LONGER SAYS "Photo on file"
 * (CTO, 2026-09-02: "is that needed, because all these photos are anyway on
 * file... there is no way to change or mark what is going to be the photo on
 * the file, the first one becomes photo on file").
 *
 * Both criticisms were right. `photoUrl`/`photoRef` are the LEGACY SINGLE-photo
 * fields, written once, so "the photo on file" was simply whichever upload
 * landed first — a designation nobody chose and nobody can change, sitting
 * above a `Weight photos` gallery on the same tab that already shows every
 * photo including that one. It read as though one image had been selected as
 * definitive.
 *
 * So: when a photo exists this states the fact and points at the gallery, which
 * is the surface that can actually show all of them and now opens them in the
 * shared viewer. When none exists it keeps the deferral — the genuinely
 * informative case, and the one the gallery states less precisely (a patient who
 * CHOSE to defer is not the same as one who was never asked).
 *
 * The legacy fields stay the INPUT because they remain the fallback the two
 * services' independent deploys rely on: a review that says a photo exists is
 * trusted even when the gallery read is empty or failed.
 *
 * ⚠️ AND IT IS NOW REACHED ONLY FOR A PATIENT WHO WAS ASKED FOR A PHOTO
 * (clinic #166, 2026-09-04). Its "Patient chose: add before dispatch" branch is
 * a statement about a CHOICE the v1/v2 question offered; a self-certified
 * patient was never offered it, so rendering this in their q14 slot would put a
 * decision they never made where their declaration belongs. `isWeightPhotoItem`
 * is what keeps it away from them — see the note there.
 */
function PhotoStateAnswer({ photoUrl, photoRef }: { photoUrl?: string | null; photoRef?: string | null }) {
  if (photoUrl || photoRef) return <>Photo supplied — see Weight photos below</>;
  return <>Patient chose: add before dispatch</>;
}

/**
 * Is this item the CONSENT DECLARATIONS answer (the q15 consent-kind question,
 * whose answer the server pre-joins with "; ")? The server's section for it is
 * `verification` — which ALSO holds the q14 weight-photo question — so the
 * tick-list and the agreed-at line must gate on THIS ITEM, never on the whole
 * section: a photo answer rendered as a signed declaration would put words in
 * the patient's mouth. The questionKey is the primary detector; the "; " join
 * is the fallback for a future question set that renames the key (no other
 * renderer uses "; " — multi-selects join with ", ").
 */
function isConsentDeclarationsItem(item: { questionKey: string; answer: string }): boolean {
  return item.questionKey === 'q15' || item.answer.includes('; ');
}

/** The questionnaire, rendered as readable Q/A sections — never raw JSON.
 * `consentAgreedAt` (2026-08-26 §2): the deal-detail consent timestamp,
 * rendered as a quiet line under the verification section's consent item —
 * nothing when absent. EXPORTED since 2026-08-27: the drawer's Questionnaire
 * tab renders these same sections as its read-only answer summary
 * (pipeline/QuestionnaireSummary.tsx) — one Q/A presentation, two mounts. */
export function StructuredQuestionnaire({
  review,
  onRetry,
  consentAgreedAt,
  photoState = false,
  renderItemExtra,
}: {
  review: Loadable<AssessmentReview>;
  onRetry: () => void;
  consentAgreedAt?: string | null;
  /**
   * Render the q14 weight-photo item as the ACTUAL photo state (thumbnail /
   * on-file note / deferral) instead of the stored answer text — the
   * Questionnaire-tab summary's mode. The Review tab keeps the stored answer
   * here (its own WeightPhotoSection card already shows the live state, and
   * §2's pinned behaviour keeps q14 a PLAIN answer beside the consent list).
   */
  photoState?: boolean;
  /**
   * Per-answer extra rendered under an item (2026-08-27): the authorise
   * flow's anchored-note affordance, threaded through the summary exactly as
   * SinglePageForm's `renderQuestionExtra` threaded it through the old
   * disabled-form replica.
   */
  renderItemExtra?: (item: { questionKey: string; prompt: string }) => ReactNode;
}) {
  if (review.state === 'loading' || review.state === 'error' || review.state === 'skip' || !review.data) {
    // The ClinicalSummary above already surfaces the loading/error/skip state; a
    // second copy would be noise. Render nothing here in those states.
    if (review.state === 'error') return <Card><ErrorState message="Sorry — we could not load the questionnaire." onRetry={onRetry} /></Card>;
    return null;
  }
  const { sections, photoUrl, photoRef } = review.data;
  // Questions an earlier check stopped at AND whose answer demonstrably differs
  // now. `answerChanged === true` only: a null (not determinable) must not be
  // rendered as a claim that the answer changed.
  const stoppedHere = new Set(
    (review.data.priorStops ?? [])
      .filter((s) => s.answerChanged === true && s.questionKey)
      .map((s) => s.questionKey as string),
  );
  if (sections.length === 0) {
    return (
      <Card>
        <SectionTitle>Questionnaire</SectionTitle>
        <Help style={{ marginBottom: 0 }}>No answers were recorded for this assessment.</Help>
      </Card>
    );
  }
  return (
    <>
      {sections.map((section) => (
        <Card key={section.key}>
          <SectionTitle>{section.title}</SectionTitle>
          <dl style={{ margin: 0 }}>
            {section.items.map((item) => (
              <div key={item.questionKey} style={{ padding: '8px 0', borderBottom: '1px solid var(--gps-border)' }}>
                <dt style={{ fontSize: '13px', color: 'var(--gps-muted)', marginBottom: '2px' }}>{item.prompt}</dt>
                <dd style={{ margin: 0, fontSize: '15px', color: 'var(--gps-text)', fontWeight: 600 }}>
                  {/* §2 — ONLY the verification section's consent-declarations
                      item (q15 / the "; "-joined answer) renders as a
                      tick-list, one line per declaration; everything else —
                      including q14's photo answer in the SAME section — keeps
                      the plain answer (see isConsentDeclarationsItem). In
                      `photoState` mode (the Questionnaire-tab summary) the q14
                      item instead shows the ACTUAL photo state — the stored
                      deferral text goes stale the moment the patient uploads. */}
                  {photoState && section.key === 'verification' && isWeightPhotoItem(item) ? (
                    <PhotoStateAnswer photoUrl={photoUrl} photoRef={photoRef} />
                  ) : section.key === 'verification' && isConsentDeclarationsItem(item) ? (
                    <ConsentAnswerList answer={item.answer} />
                  ) : (
                    item.answer
                  )}
                  {item.detail ? (
                    <span style={{ display: 'block', fontWeight: 400, fontSize: '13.5px', color: 'var(--gps-muted)', marginTop: '2px' }}>{item.detail}</span>
                  ) : null}
                  {stoppedHere.has(item.questionKey) ? (
                    // STATIC TEXT, no live region. The band above already
                    // announced this once; N live regions on one surface would be
                    // far worse than none, and static text is read in natural
                    // order. Words, never colour alone.
                    <span
                      style={{
                        display: 'block',
                        fontWeight: 400,
                        fontSize: '13.5px',
                        color: 'var(--gps-warn-text, var(--gps-muted))',
                        marginTop: '2px',
                      }}
                    >
                      Answer changed — an earlier check was stopped at this question.
                    </span>
                  ) : null}
                  {/* The per-answer affordance (notes-per-answer, Wave 4 §4)
                      rides under the answer it belongs to — normal font
                      weight, or the toggle inherits the answer's bold. */}
                  {renderItemExtra ? (
                    <span style={{ display: 'block', fontWeight: 400 }}>
                      {renderItemExtra({ questionKey: item.questionKey, prompt: item.prompt })}
                    </span>
                  ) : null}
                </dd>
              </div>
            ))}
          </dl>
          {/* §2 — WHEN the patient agreed: under the verification section,
              and ONLY when the consent-declarations item is actually in it —
              the timestamp describes those ticks, and pinning it to a section
              that happens to hold only the q14 photo answer would claim an
              agreement the screen does not show. A quiet, factual line (from
              the deal detail's consentAgreedAt); renders nothing when the
              server has no timestamp — absence must never read as "not
              agreed". */}
          {section.key === 'verification' &&
          consentAgreedAt &&
          section.items.some(isConsentDeclarationsItem) ? (
            <p style={{ color: 'var(--gps-muted)', fontSize: '12.5px', margin: 'var(--gps-s-2) 0 0' }}>
              Agreed by the patient — {formatUtcDateTime(consentAgreedAt)}
            </p>
          ) : null}
        </Card>
      ))}
    </>
  );
}
